import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Download, Edit, FileText, Filter, ImageIcon, Link2, MessageSquare, Music4, Paperclip, Plus, Reply, Send, Video, X, XCircle, Calendar, User, Eye } from 'lucide-react';
import { supabase } from '../src/lib/supabase';
import { DealershipTask, Employee, TaskCommentMediaType, MessageReaction } from '../types';
import LoadingButton from '../src/components/ui/LoadingButton';
import TaskDetailsScreen from './TaskDetailsScreen';
import MessageReactionChip from './MessageReactionChip';
import { DatabaseTask, transformTaskToApp } from '../src/utils/transformers';
import { logSupabaseQueryError } from '../src/utils/errorLogging';
import { sendApprovalRequestNotification } from '../src/utils/pushNotifications';

type ApprovalStatus = 'PENDING' | 'NEEDS_REVIEW' | 'APPROVED' | 'REJECTED';
type ApprovalView = 'my_requests' | 'needs_my_approval';

type ApprovalItem = {
  id: string;
  requester_id: string;
  requester?: string | null;
  requester_name?: string | null;
  approver_id: string;
  title: string;
  description: string;
  amount: number | null;
  status: ApprovalStatus;
  created_at?: string | null;
  updated_at?: string | null;
  company_id?: string | null;
  task_id?: string | null;
  isEscalated: boolean;
  adminEscalationStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  escalated_to?: string | null;
  linkedTask?: DealershipTask | null;
};

type ApprovalThread = {
  id: string;
  approval_id: string;
  sender_id: string;
  message_text: string;
  created_at: string;
  reply_to_id?: string | null;
  media_url?: string | null;
  media_type?: TaskCommentMediaType | null;
  media_name?: string | null;
  media_size?: number | null;
  is_edited?: boolean;
  edited_at?: string | null;
};

type ApprovalThreadView = ApprovalThread & {
  sender_name: string;
  created_at_ms: number;
  reply_to?: ApprovalThreadQuote | null;
  optimistic?: boolean;
  upload_progress?: number;
};

type ApprovalThreadQuote = {
  id: string;
  sender_name: string;
  message_text: string;
  media_type?: TaskCommentMediaType | null;
  media_name?: string | null;
};

type MentionCandidate = {
  id: string;
  name: string;
};

type PendingApprovalChatMedia = {
  id: string;
  file: File;
  mediaType: TaskCommentMediaType;
  name: string;
  size: number;
  previewUrl: string | null;
};

type ApproverOption = {
  id: string;
  name: string;
  role: string;
};

type ApprovalAttachment = {
  name: string;
  url: string;
  contentType: string;
  size: number;
};

type PendingAttachment = {
  id: string;
  name: string;
  contentType: string;
  size: number;
};

const LOCKED_STATUSES: ApprovalStatus[] = ['APPROVED', 'REJECTED'];
const ATTACHMENT_LINE_PREFIX = '__ATTACHMENT__|';
const APPROVAL_ATTACHMENT_BUCKET = 'task-proofs';
const HIGH_PRIORITY_COMPLETION_PREFIX = 'High Priority Task Completion:';
const EXTENSION_REQUEST_PREFIX = 'Deadline Extension Request:';
const CHAT_MEDIA_BUCKET = 'chat-media';
const MAX_CHAT_MEDIA_BYTES = 50 * 1024 * 1024;
const normalizeApprovalTitle = (value: unknown): string =>
  String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const normalizeStatus = (value: unknown): ApprovalStatus => {
  if (value === 'PENDING' || value === 'NEEDS_REVIEW' || value === 'APPROVED' || value === 'REJECTED') {
    return value;
  }
  return 'PENDING';
};

const getStatusClasses = (status: ApprovalStatus): string => {
  if (status === 'APPROVED') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-50 text-rose-700 border border-rose-200';
  if (status === 'NEEDS_REVIEW') return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-[var(--accent-light)] text-[var(--accent)] border border-[var(--accent)]/20';
};

const formatAmount = (value: number | null): string => {
  if (value === null || value === undefined) {
    return 'Non-monetary';
  }

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(value || 0);
  } catch {
    return `INR ${value || 0}`;
  }
};

const formatDateTime = (value: string): string => {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Date(parsed).toLocaleString();
};

const formatTaskDueDate = (deadline?: number): string => {
  if (!deadline) return 'No due date';
  const parsed = new Date(deadline);
  return Number.isNaN(parsed.getTime()) ? 'No due date' : parsed.toLocaleString();
};

const extractApprovalAttachments = (rawDescription: string): { description: string; attachments: ApprovalAttachment[] } => {
  const lines = String(rawDescription || '').split('\n');
  const attachments: ApprovalAttachment[] = [];
  const textLines: string[] = [];

  for (const line of lines) {
    if (!line.startsWith(ATTACHMENT_LINE_PREFIX)) {
      textLines.push(line);
      continue;
    }

    const payload = line.slice(ATTACHMENT_LINE_PREFIX.length);
    const [encodedName, encodedUrl, encodedType, rawSize] = payload.split('|');
    const safeDecode = (value: string): string => {
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    };
    const name = safeDecode(encodedName || '').trim();
    const url = safeDecode(encodedUrl || '').trim();
    const contentType = safeDecode(encodedType || '').trim();
    const size = Number(rawSize || 0);

    if (!name || !url) continue;
    attachments.push({
      name,
      url,
      contentType,
      size: Number.isFinite(size) ? size : 0,
    });
  }

  return {
    description: textLines.join('\n').trim(),
    attachments,
  };
};

const appendAttachmentsToDescription = (description: string, attachments: ApprovalAttachment[]): string => {
  if (!attachments.length) return description;

  const attachmentLines = attachments.map((item) => {
    const encodedName = encodeURIComponent(item.name);
    const encodedUrl = encodeURIComponent(item.url);
    const encodedType = encodeURIComponent(item.contentType || '');
    const encodedSize = String(Number.isFinite(item.size) ? item.size : 0);
    return `${ATTACHMENT_LINE_PREFIX}${encodedName}|${encodedUrl}|${encodedType}|${encodedSize}`;
  });

  return [description, ...attachmentLines].filter(Boolean).join('\n');
};

const formatAttachmentSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const sanitizeFileName = (name: string): string => String(name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');

const isMissingThreadColumnError = (error: unknown): boolean => {
  const message = String((error as any)?.message || '').toLowerCase();
  return message.includes('column') && message.includes('does not exist');
};

const isMessageWithinEditWindow = (createdAt: string): boolean =>
  Date.now() - new Date(createdAt).getTime() < 60 * 60 * 1000;

const getMediaTypeFromFile = (file: File): TaskCommentMediaType => {
  const mimeType = String(file.type || '').toLowerCase();
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'file';
};

const isBlobUrl = (value: string | null | undefined): boolean =>
  String(value || '').startsWith('blob:');

const formatChatFileSize = (bytes: number | null | undefined): string => {
  const safeBytes = Number(bytes || 0);
  if (!Number.isFinite(safeBytes) || safeBytes <= 0) return '0 B';
  if (safeBytes < 1024) return `${safeBytes} B`;
  if (safeBytes < 1024 * 1024) return `${(safeBytes / 1024).toFixed(1)} KB`;
  return `${(safeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getChatDateLabel = (timestamp: number): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const matchesDay = (left: Date, right: Date) =>
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();
  if (matchesDay(date, today)) return 'Today';
  if (matchesDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
};

const getQuotedMessageText = (message: {
  message_text?: string | null;
  media_type?: TaskCommentMediaType | null;
  media_name?: string | null;
}): string => {
  const text = String(message.message_text || '').trim();
  if (text) return text.length > 72 ? `${text.slice(0, 72)}...` : text;
  if (message.media_type === 'image') return 'Photo';
  if (message.media_type === 'video') return 'Video';
  if (message.media_type === 'audio') return 'Audio';
  if (message.media_name) return message.media_name;
  return 'Attachment';
};

const getMentionQuery = (text: string, caretIndex: number): { start: number; query: string } | null => {
  const beforeCaret = text.slice(0, caretIndex);
  const atIndex = beforeCaret.lastIndexOf('@');
  if (atIndex < 0) return null;
  const query = beforeCaret.slice(atIndex + 1);
  if (query.includes('\n') || !/^[\w.\- ]{0,40}$/.test(query)) return null;
  return { start: atIndex, query: query.trimStart() };
};

const parseDateTimeMs = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const direct = Number(value);
    if (Number.isFinite(direct)) return direct;
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const approvalActivityMs = (item: ApprovalItem): number =>
  Math.max(parseDateTimeMs(item.updated_at), parseDateTimeMs(item.created_at));

const sortApprovalsByRecency = (items: ApprovalItem[]): ApprovalItem[] =>
  [...items].sort((a, b) => {
    const diff = approvalActivityMs(b) - approvalActivityMs(a);
    if (diff !== 0) return diff;
    return String(b.id).localeCompare(String(a.id));
  });

const approvalsAreEqual = (left: ApprovalItem[], right: ApprovalItem[]): boolean => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    const a = left[i];
    const b = right[i];
    const aTask = a.linkedTask;
    const bTask = b.linkedTask;
    if (
      a.id !== b.id ||
      a.requester_id !== b.requester_id ||
      a.approver_id !== b.approver_id ||
      a.title !== b.title ||
      a.description !== b.description ||
      a.status !== b.status ||
      a.amount !== b.amount ||
      a.company_id !== b.company_id ||
      a.task_id !== b.task_id ||
      a.created_at !== b.created_at ||
      a.updated_at !== b.updated_at ||
      aTask?.id !== bTask?.id ||
      aTask?.description !== bTask?.description ||
      aTask?.assignedTo !== bTask?.assignedTo ||
      aTask?.deadline !== bTask?.deadline ||
      aTask?.status !== bTask?.status
    ) {
      return false;
    }
  }
  return true;
};

const getActionErrorMessage = (error: unknown, fallback = 'Operation failed.'): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message?: unknown }).message || '').trim();
    if (message) {
      return message;
    }
  }
  return fallback;
};

const isHighPriorityTaskCompletionApproval = (approval: ApprovalItem): boolean =>
  normalizeApprovalTitle(approval.title).startsWith('high priority task completion');

const isTaskExtensionApproval = (approval: ApprovalItem): boolean =>
  normalizeApprovalTitle(approval.title).startsWith('deadline extension request');

const isExtensionClosedForLinkedTask = (approval: ApprovalItem): boolean => {
  if (!approval.linkedTask) {
    return false;
  }

  const linkedTaskStatus = String(approval.linkedTask?.status || '').toLowerCase();
  const extensionStatus = String(approval.linkedTask?.extensionStatus || '').toUpperCase();

  return (
    linkedTaskStatus === 'completed' ||
    extensionStatus === 'APPROVED' ||
    extensionStatus === 'REJECTED'
  );
};

const isHighPriorityCompletionStaleForLinkedTask = (approval: ApprovalItem): boolean => {
  if (!approval.linkedTask) {
    return false;
  }

  const linkedTaskStatus = String(approval.linkedTask.status || '').toLowerCase();
  const requesterId = String(approval.requester_id || '').trim();
  const approverId = String(approval.approver_id || '').trim();
  const taskAssigneeId = String(approval.linkedTask.assignedTo || '').trim();
  const taskCreatorId = String(approval.linkedTask.assignedBy || '').trim();

  return (
    linkedTaskStatus !== 'pending_approval' ||
    !requesterId ||
    !approverId ||
    !taskAssigneeId ||
    !taskCreatorId ||
    requesterId !== taskAssigneeId ||
    approverId !== taskCreatorId
  );
};

const threadsAreEqual = (left: ApprovalThreadView[], right: ApprovalThreadView[]): boolean => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    const a = left[i];
    const b = right[i];
    if (
      a.id !== b.id ||
      a.approval_id !== b.approval_id ||
      a.sender_id !== b.sender_id ||
      a.sender_name !== b.sender_name ||
      a.message_text !== b.message_text ||
      a.created_at !== b.created_at ||
      a.reply_to_id !== b.reply_to_id ||
      a.media_url !== b.media_url ||
      a.media_type !== b.media_type ||
      a.media_name !== b.media_name ||
      a.media_size !== b.media_size ||
      a.is_edited !== b.is_edited ||
      a.edited_at !== b.edited_at
    ) {
      return false;
    }
  }
  return true;
};

// Helper function to filter approvals by status
const filterApprovalsByStatus = (approvals: ApprovalItem[], filter: 'all' | 'pending' | 'completed'): ApprovalItem[] => {
  if (filter === 'all') return approvals;
  if (filter === 'pending') {
    return approvals.filter(approval => 
      approval.status === 'PENDING' || approval.status === 'NEEDS_REVIEW'
    );
  }
  if (filter === 'completed') {
    return approvals.filter(approval => 
      approval.status === 'APPROVED' || approval.status === 'REJECTED'
    );
  }
  return approvals;
};

const normalizeApprovalStatusKey = (status: unknown): string => String(status || '').toUpperCase();

const isPendingApprovalStatus = (status: unknown): boolean => {
  const key = normalizeApprovalStatusKey(status);
  return key === 'PENDING' || key === 'NEEDS_REVIEW' || key === 'IN_PROGRESS' || key === 'IN-PROGRESS';
};

const isApprovedApprovalStatus = (status: unknown): boolean => {
  const key = normalizeApprovalStatusKey(status);
  return key === 'APPROVED' || key === 'REJECTED';
};

const getApprovalUpdatedAtValue = (approval: ApprovalItem): string | null => {
  const camel = (approval as any).updatedAt;
  if (typeof camel === 'string' && camel) return camel;
  return approval.updated_at || null;
};

// Helper function to group approvals by month and year
const groupApprovalsByMonth = (approvals: ApprovalItem[]): Record<string, ApprovalItem[]> => {
  return approvals.reduce((groups, approval) => {
    const date = new Date(approval.created_at || '');
    const monthYear = date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
    
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(approval);
    return groups;
  }, {} as Record<string, ApprovalItem[]>);
};

// Helper function to get available months from approvals
const getAvailableMonths = (approvals: ApprovalItem[]): string[] => {
  const months = new Set<string>();
  approvals.forEach(approval => {
    if (approval.created_at) {
      const date = new Date(approval.created_at);
      const monthYear = date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long' 
      });
      months.add(monthYear);
    }
  });
  return Array.from(months).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
};

// Helper function to filter approvals by month
const filterApprovalsByMonth = (approvals: ApprovalItem[], monthFilter: string): ApprovalItem[] => {
  if (monthFilter === 'all') return approvals;
  
  return approvals.filter(approval => {
    if (!approval.created_at) return false;
    const date = new Date(approval.created_at);
    const monthYear = date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
    return monthYear === monthFilter;
  });
};

// Helper function to filter approvals by date range
const filterApprovalsByDateRange = (approvals: ApprovalItem[], startDate: string, endDate: string): ApprovalItem[] => {
  if (!startDate && !endDate) return approvals;
  
  return approvals.filter(approval => {
    if (!approval.created_at) return false;
    const approvalDate = new Date(approval.created_at);
    const start = startDate ? new Date(startDate) : new Date('1970-01-01');
    const end = endDate ? new Date(endDate + 'T23:59:59') : new Date();
    
    return approvalDate >= start && approvalDate <= end;
  });
};

// Helper function to get approver name by ID
const getApproverName = (approverId: string, approvers: ApproverOption[]): string => {
  const approver = approvers.find(a => a.id === approverId);
  return approver ? approver.name : 'Unknown';
};

interface ApprovalsPanelProps {
  currentUser: Employee;
  deepLinkApprovalTarget?: {
    approvalId: string;
    token: number;
  } | null;
  onDeepLinkApprovalHandled?: () => void;
}

function AccountantApprovalsView({ companyId, currentUser }: { companyId: string; currentUser: Employee }) {
  const [myApprovals, setMyApprovals] = useState<any[]>([]);
  const [companyApprovals, setCompanyApprovals] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'my' | 'company'>('my');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [dateFilter, setDateFilter] = useState<'today' | 'yesterday' | 'last7' | 'custom'>('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showDateFilterMenu, setShowDateFilterMenu] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvers, setApprovers] = useState<ApproverOption[]>([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [creatingApproval, setCreatingApproval] = useState(false);
  const [requestTitle, setRequestTitle] = useState('');
  const [requestDescription, setRequestDescription] = useState('');
  const [requestAmount, setRequestAmount] = useState('');
  const [requestApproverId, setRequestApproverId] = useState('');
  const [requestAttachments, setRequestAttachments] = useState<PendingAttachment[]>([]);
  const [employeeNamesById, setEmployeeNamesById] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const dateFilterRef = useRef<HTMLDivElement | null>(null);
  const attachmentsInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentFilesRef = useRef<Record<string, File>>({});

  const syncInputOnPickerClose = (input: HTMLInputElement | null, setter: (value: string) => void) => {
    if (!input) return;
    const tick = () => {
      if (document.activeElement === input) {
        requestAnimationFrame(tick);
      } else {
        setter(input.value);
      }
    };
    requestAnimationFrame(tick);
  };

  useEffect(() => {
    const fetchApprovals = async () => {
      setLoading(true);
      const approvalSelectFields =
        'id, title, description, amount, status, created_at, requester_id, approver_id, isEscalated, adminEscalationStatus, escalated_to';
      const myQuery = supabase
        .from('approvals')
        .select(approvalSelectFields)
        .eq('company_id', companyId)
        .eq('requester_id', currentUser.id)
        .order('created_at', { ascending: false });

      let companyQuery = supabase
        .from('approvals')
        .select(approvalSelectFields)
        .eq('company_id', companyId)
        .neq('requester_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (dateFilter === 'today') {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        companyQuery = companyQuery.gte('created_at', startOfDay.toISOString());
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        const endOfYesterday = new Date(yesterday);
        endOfYesterday.setHours(23, 59, 59, 999);
        companyQuery = companyQuery.gte('created_at', yesterday.toISOString()).lte('created_at', endOfYesterday.toISOString());
      } else if (dateFilter === 'last7') {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        sevenDaysAgo.setHours(0, 0, 0, 0);
        companyQuery = companyQuery.gte('created_at', sevenDaysAgo.toISOString());
      } else if (dateFilter === 'custom') {
        if (fromDate) {
          const customStart = new Date(`${fromDate}T00:00:00`);
          companyQuery = companyQuery.gte('created_at', customStart.toISOString());
        }
        if (toDate) {
          const customEnd = new Date(`${toDate}T00:00:00`);
          customEnd.setHours(23, 59, 59, 999);
          companyQuery = companyQuery.lte('created_at', customEnd.toISOString());
        }
      }

      const [myResult, companyResult] = await Promise.all([myQuery, companyQuery]);

      if (!myResult.error) {
        setMyApprovals(myResult.data || []);
      } else {
        console.warn('Failed to load accountant requests:', myResult.error);
        logSupabaseQueryError(myResult.error);
      }

      if (!companyResult.error) {
        setCompanyApprovals(companyResult.data || []);
      } else {
        console.warn('Failed to load accountant approvals:', companyResult.error);
        logSupabaseQueryError(companyResult.error);
      }

      setLoading(false);
    };

    void fetchApprovals();
  }, [companyId, currentUser.id, dateFilter, fromDate, toDate, reloadToken]);

  useEffect(() => {
    const loadApprovers = async () => {
      setLoadingApprovers(true);
      setError(null);
      try {
        const { data, error: approverError } = await supabase
          .from('employees')
          .select('id, name, role')
          .eq('company_id', companyId)
          .order('name', { ascending: true });

        if (approverError) {
          throw approverError;
        }

        const directory = (data || [])
          .map((row: any) => ({
            id: String(row.id || ''),
            name: String(row.name || 'Unknown'),
            role: String(row.role || 'staff'),
          }))
          .filter((item) => item.id);

        const candidates = directory
          .filter((item) => item.id && item.role !== 'staff')
          .sort((a, b) => a.name.localeCompare(b.name));

        setEmployeeNamesById(directory.reduce<Record<string, string>>((acc, item) => {
          acc[item.id] = item.name;
          return acc;
        }, {}));
        setApprovers(candidates);
        setRequestApproverId((prev) => prev || candidates[0]?.id || '');
      } catch (loadApproverErr: any) {
        setError(loadApproverErr?.message || 'Failed to load approvers.');
      } finally {
        setLoadingApprovers(false);
      }
    };

    void loadApprovers();
  }, [companyId]);

  useEffect(() => {
    if (!showDateFilterMenu) return;
    const handleOutside = (event: MouseEvent) => {
      if (!dateFilterRef.current) return;
      const target = event.target as Node;
      if (!dateFilterRef.current.contains(target)) {
        setShowDateFilterMenu(false);
      }
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [showDateFilterMenu]);

  const getNormalizedStatus = (status: unknown) => String(status || '').trim().toLowerCase();

  const currentApprovals = activeTab === 'my' ? myApprovals : companyApprovals;
  const filtered = filter === 'all'
    ? currentApprovals
    : currentApprovals.filter((approval) => getNormalizedStatus(approval.status) === filter);

  const counts = {
    all: currentApprovals.length,
    pending: currentApprovals.filter((approval) => getNormalizedStatus(approval.status) === 'pending').length,
    approved: currentApprovals.filter((approval) => getNormalizedStatus(approval.status) === 'approved').length,
    rejected: currentApprovals.filter((approval) => getNormalizedStatus(approval.status) === 'rejected').length,
  };

  const renderAccountantApprovalCard = (approval: any) => {
    const personId = activeTab === 'my'
      ? String(approval.approver_id || '').trim()
      : String(approval.requester_id || '').trim();
    const personLabel = activeTab === 'my' ? 'Approver' : 'Requester';
    const parsedApproval = extractApprovalAttachments(String(approval.description || ''));
    const approvalStatus = normalizeStatus(approval.status);
    const finalApproverId = String(approval.approver_id || '').trim();
    const escalatedApproverId = String(approval.escalated_to || '').trim();
    const includeEscalatedApprover =
      Boolean(escalatedApproverId) &&
      (approval.isEscalated || String(approval.adminEscalationStatus || '').toUpperCase() === 'APPROVED');
    const decisionApproverIds =
      approvalStatus === 'APPROVED' || approvalStatus === 'REJECTED'
        ? Array.from(
            new Set(
              [
                ...(includeEscalatedApprover ? [escalatedApproverId] : []),
                finalApproverId,
              ].filter(Boolean)
            )
          )
        : [];
    const decisionApproverNames = decisionApproverIds.map((approverId) => employeeNamesById[approverId] || 'Unknown');

    return (
      <div
        key={approval.id}
        className="w-full rounded-2xl border border-[var(--border)] bg-white border-l-[3px] border-l-[var(--orange)] p-4 transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-[var(--ink)] truncate">{approval.title || 'Untitled'}</p>
          <span className={`font-ui-mono text-[10px] font-medium uppercase px-2 py-1 rounded-full ${getStatusClasses(approvalStatus)}`}>
            {approvalStatus}
          </span>
        </div>
        <p className="text-sm font-bold text-[var(--accent)] mt-1">
          {formatAmount(approval.amount === null || approval.amount === undefined ? null : Number(approval.amount))}
        </p>
        <p className="text-xs text-[var(--ink-3)] mt-1 line-clamp-2">
          {parsedApproval.description || 'No description'}
        </p>
        <div className="mt-2 flex flex-col gap-1 text-xs text-slate-600">
          <div className="flex items-center gap-1">
            <span className="font-medium">Created:</span>
            <span>{approval.created_at ? formatDateTime(String(approval.created_at)).split(',')[0] : 'N/A'}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium">{personLabel}:</span>
            <span>{employeeNamesById[personId] || 'Unknown'}</span>
          </div>
          {decisionApproverNames.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="font-medium">{approvalStatus === 'APPROVED' ? 'Approved by:' : 'Rejected by:'}</span>
              <span>{decisionApproverNames.join(', ')}</span>
            </div>
          )}
        </div>
        {parsedApproval.attachments.length > 0 && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Attached Documents</p>
            <div className="mt-2 space-y-1.5">
              {parsedApproval.attachments.map((attachment, index) => (
                <a
                  key={`${attachment.url}_${index}`}
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">{attachment.name}</p>
                    <p className="text-[10px] text-slate-500">
                      {attachment.contentType || 'File'}
                      {attachment.size ? ` - ${formatAttachmentSize(attachment.size)}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-slate-500 shrink-0">
                    <Link2 className="w-3.5 h-3.5" />
                    <Download className="w-3.5 h-3.5" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const dateFilterLabel =
    dateFilter === 'today'
      ? 'Today'
      : dateFilter === 'yesterday'
      ? 'Yesterday'
      : dateFilter === 'last7'
      ? 'Last 7 days'
      : 'Custom range';

  const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error('Operation timed out')), ms)
      ),
    ]);
  };

  const handleCreateRequest = async () => {
    const title = requestTitle.trim();
    const description = requestDescription.trim();
    const rawAmount = requestAmount.trim();
    const amountValue = rawAmount === '' ? null : Number(rawAmount);
    const approverId = requestApproverId.trim();

    if (creatingApproval) return;
    if (!title) {
      setError('Approval title is required.');
      return;
    }
    if (!approverId) {
      setError('Please choose an approver to tag.');
      return;
    }
    if (amountValue !== null && (!Number.isFinite(amountValue) || amountValue < 0)) {
      setError('Amount must be a valid non-negative number when provided.');
      return;
    }

    setCreatingApproval(true);
    setError(null);

    try {
      let uploadedAttachments: ApprovalAttachment[] = [];
      if (requestAttachments.length > 0) {
        const uploadResults = await Promise.all(
          requestAttachments.map(async (attachment) => {
            const file = attachmentFilesRef.current[attachment.id];
            if (!file) {
              throw new Error(`Attachment "${attachment.name}" is missing. Please re-attach and try again.`);
            }

            const safeName = sanitizeFileName(attachment.name);
            const storagePath = `approval-attachments/${companyId}/${currentUser.id}/${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 9)}-${safeName}`;

            const { error: uploadError } = await supabase.storage
              .from(APPROVAL_ATTACHMENT_BUCKET)
              .upload(storagePath, file, {
                contentType: file.type || 'application/octet-stream',
                upsert: false,
              });

            if (uploadError) {
              throw new Error(
                `Failed to upload "${file.name}". Ensure Storage bucket "${APPROVAL_ATTACHMENT_BUCKET}" allows uploads.`
              );
            }

            const { data: publicData } = supabase.storage
              .from(APPROVAL_ATTACHMENT_BUCKET)
              .getPublicUrl(storagePath);

            return {
              name: attachment.name,
              url: String(publicData?.publicUrl || '').trim(),
              contentType: file.type || attachment.contentType || '',
              size: file.size || attachment.size || 0,
            } as ApprovalAttachment;
          })
        );

        uploadedAttachments = uploadResults.filter((item) => Boolean(item.url));
      }

      const { error: createError } = await withTimeout(
        supabase
          .from('approvals')
          .insert({
            requester_id: currentUser.id,
            approver_id: approverId,
            title,
            description: appendAttachmentsToDescription(description, uploadedAttachments),
            amount: amountValue,
            status: 'PENDING',
            company_id: companyId,
          }),
        30000
      );

      if (createError) throw createError;

      setRequestTitle('');
      setRequestDescription('');
      setRequestAmount('');
      setRequestAttachments([]);
      attachmentFilesRef.current = {};
      setIsApprovalModalOpen(false);
      setReloadToken((prev) => prev + 1);
    } catch (createErr: any) {
      setError(createErr?.message || 'Failed to create approval request.');
    } finally {
      setCreatingApproval(false);
    }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">Approvals</p>
          <h1 className="text-xl font-bold text-gray-900">
            {activeTab === 'my' ? 'My Requests' : 'Company Approvals'}
          </h1>
          {activeTab === 'company' && <p className="text-xs text-gray-400 mt-1">Read-only view</p>}
        </div>
        <button
          type="button"
          onClick={() => setIsApprovalModalOpen(true)}
          className="w-full sm:w-auto min-h-[44px] bg-[var(--accent)] hover:bg-[#4338CA] text-white rounded-2xl px-4 py-3 sm:py-2.5 shadow-[0_4px_14px_rgba(79,70,229,0.15)] flex items-center justify-center gap-2 transition-all duration-200 active:scale-95"
        >
          <Plus className="w-5 h-5" />
          <span className="font-semibold">Approval</span>
        </button>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2 mb-4">
        <button
          type="button"
          onClick={() => {
            setActiveTab('my');
            setFilter('all');
          }}
          className={`w-full sm:w-auto min-h-[44px] px-3 py-2 rounded-xl text-sm font-bold border inline-flex items-center justify-center gap-2 ${
            activeTab === 'my'
              ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
              : 'bg-white text-slate-700 border-slate-200'
          }`}
        >
          My Requests
          {myApprovals.length > 0 && (
            <span className="font-ui-mono inline-flex min-w-[22px] h-[22px] px-1.5 items-center justify-center rounded-full bg-white/20 text-[11px] font-medium leading-none">
              {myApprovals.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab('company');
            setFilter('all');
          }}
          className={`w-full sm:w-auto min-h-[44px] px-3 py-2 rounded-xl text-sm font-bold border inline-flex items-center justify-center gap-2 ${
            activeTab === 'company'
              ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
              : 'bg-white text-slate-700 border-slate-200'
          }`}
        >
          Company Approvals
          {companyApprovals.length > 0 && (
            <span className="font-ui-mono inline-flex min-w-[22px] h-[22px] px-1.5 items-center justify-center rounded-full bg-white/20 text-[11px] font-medium leading-none">
              {companyApprovals.length}
            </span>
          )}
        </button>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex gap-2 flex-wrap">
          {(['all', 'pending', 'approved', 'rejected'] as const).map((nextFilter) => (
            <button
              key={nextFilter}
              type="button"
              onClick={() => setFilter(nextFilter)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                filter === nextFilter
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-500 border-gray-200'
              }`}
            >
              {nextFilter.charAt(0).toUpperCase() + nextFilter.slice(1)} ({counts[nextFilter]})
            </button>
          ))}
        </div>

        {activeTab === 'company' && (
        <div className="relative self-end sm:self-auto" ref={dateFilterRef}>
          <button
            type="button"
            onClick={() => setShowDateFilterMenu((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-xl border border-[var(--border)] bg-white px-3 py-2 text-xs font-semibold text-[var(--ink-2)] hover:bg-slate-50 transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            title="Filter approvals by date"
          >
            <Filter className="w-3.5 h-3.5" />
            <span>{dateFilterLabel}</span>
          </button>

          {showDateFilterMenu && (
            <div className="absolute right-0 mt-2 w-60 rounded-xl border border-slate-200 bg-white p-2 shadow-lg z-20 space-y-1">
              <button
                type="button"
                onClick={() => {
                  setDateFilter('today');
                  setShowDateFilterMenu(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  dateFilter === 'today' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  setDateFilter('yesterday');
                  setShowDateFilterMenu(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  dateFilter === 'yesterday' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Yesterday
              </button>
              <button
                type="button"
                onClick={() => {
                  setDateFilter('last7');
                  setShowDateFilterMenu(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  dateFilter === 'last7' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Last 7 days
              </button>
              <button
                type="button"
                onClick={() => setDateFilter('custom')}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  dateFilter === 'custom' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Custom range
              </button>

              {dateFilter === 'custom' && (
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-2">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">From</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(event) => setFromDate(event.target.value)}
                        onInput={(event) => setFromDate(event.currentTarget.value)}
                        onBlur={(event) => setFromDate(event.currentTarget.value)}
                        onFocus={(event) => syncInputOnPickerClose(event.currentTarget, setFromDate)}
                        className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-900"
                      />
                      {fromDate && (
                        <button
                          type="button"
                          onClick={() => setFromDate('')}
                          className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
                          aria-label="Clear date"
                        >
                          X Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={toDate}
                        onChange={(event) => setToDate(event.target.value)}
                        onInput={(event) => setToDate(event.currentTarget.value)}
                        onBlur={(event) => setToDate(event.currentTarget.value)}
                        onFocus={(event) => syncInputOnPickerClose(event.currentTarget, setToDate)}
                        className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-900"
                      />
                      {toDate && (
                        <button
                          type="button"
                          onClick={() => setToDate('')}
                          className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
                          aria-label="Clear date"
                        >
                          X Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-400 text-sm">No approvals found.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((approval) => renderAccountantApprovalCard(approval))}
        </div>
      )}

      {isApprovalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={() => !creatingApproval && setIsApprovalModalOpen(false)}
          />

          <div className="relative bg-white w-full max-w-lg sm:max-w-xl h-[90vh] sm:h-auto rounded-t-3xl sm:rounded-2xl shadow-xl overflow-y-auto animate-in fade-in zoom-in-95">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 sm:p-5 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[var(--accent)]" />
                Create Approval Request
              </h2>
              {!creatingApproval && (
                <button
                  type="button"
                  onClick={() => setIsApprovalModalOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="p-4 sm:p-6 space-y-3">
              <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">Create Request</p>
              <div className="grid grid-cols-1 gap-2">
                <input
                  value={requestTitle}
                  onChange={(event) => setRequestTitle(event.target.value)}
                  placeholder="Request title"
                  className="min-h-[48px] px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
                  autoFocus
                />
                <div className="relative">
                  <textarea
                    value={requestDescription}
                    onChange={(event) => setRequestDescription(event.target.value)}
                    placeholder="Description"
                    className="min-h-[90px] w-full px-3 py-2 pb-10 rounded-xl border border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-900/20 resize-none"
                  />
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      attachmentsInputRef.current?.click();
                    }}
                    className="absolute bottom-2 right-2 p-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
                    title="Attach files"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    ref={attachmentsInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.xls,.xlsx,.csv,image/*"
                    className="hidden"
                    onChange={(event) => {
                      try {
                        const files = Array.from(event.target.files || []);
                        if (!files.length) return;

                        setRequestAttachments((prev) => {
                          const existing = new Set(prev.map((item) => `${item.name}_${item.size}`));
                          const next = [...prev];

                          for (const file of files) {
                            const key = `${file.name}_${file.size}`;
                            if (existing.has(key)) continue;

                            const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
                            attachmentFilesRef.current[id] = file;
                            next.push({
                              id,
                              name: file.name,
                              contentType: file.type || '',
                              size: file.size || 0,
                            });
                            existing.add(key);
                          }

                          return next;
                        });
                      } catch (attachError: any) {
                        setError(attachError?.message || 'Failed to attach selected files.');
                      } finally {
                        event.currentTarget.value = '';
                      }
                    }}
                  />
                </div>
                {requestAttachments.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {requestAttachments.map((file, index) => (
                      <div key={`${file.name}_${file.size}_${index}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
                        <p className="text-xs text-slate-700 truncate">
                          {file.name}
                          {file.size ? ` (${formatAttachmentSize(file.size)})` : ''}
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setRequestAttachments((prev) => {
                              const toRemove = prev[index];
                              if (toRemove?.id) {
                                delete attachmentFilesRef.current[toRemove.id];
                              }
                              return prev.filter((_, i) => i !== index);
                            })
                          }
                          className="p-1 rounded-md text-slate-500 hover:bg-slate-200"
                          title="Remove attachment"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    value={requestAmount}
                    onChange={(event) => setRequestAmount(event.target.value)}
                    placeholder="Amount (INR)"
                    type="number"
                    min="0"
                    className="min-h-[48px] px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
                  />
                  <select
                    value={requestApproverId}
                    onChange={(event) => setRequestApproverId(event.target.value)}
                    className="min-h-[48px] px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
                  >
                    {loadingApprovers ? (
                      <option value="">Loading approvers...</option>
                    ) : approvers.length === 0 ? (
                      <option value="">No approver available</option>
                    ) : (
                      approvers.map((approver) => (
                        <option key={approver.id} value={approver.id}>
                          {approver.name} ({approver.role})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsApprovalModalOpen(false)}
                    disabled={creatingApproval}
                    className="flex-1 min-h-[48px] rounded-xl bg-slate-100 text-slate-700 text-base font-bold hover:bg-slate-200 transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <LoadingButton
                    type="button"
                    onClick={() => void handleCreateRequest()}
                    isLoading={creatingApproval}
                    loadingText="Creating..."
                    variant="primary"
                    className="flex-1 min-h-[48px] rounded-xl bg-[var(--accent)] hover:bg-[#4338CA] text-white text-base font-bold disabled:opacity-60"
                  >
                    Create & Tag Approver
                  </LoadingButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 text-xs text-rose-700">
          {error}
        </div>
      )}
    </div>
  );
}

const ApprovalsPanel: React.FC<ApprovalsPanelProps> = ({
  currentUser,
  deepLinkApprovalTarget = null,
  onDeepLinkApprovalHandled,
}) => {
  const isAccountant = currentUser?.role === 'accountant';

  if (isAccountant) {
    return <AccountantApprovalsView companyId={currentUser.company_id} currentUser={currentUser} />;
  }

  type ApprovedDateFilter = 'today' | 'yesterday' | 'last7' | 'custom';
  const isAdminApprover = currentUser.role === 'owner' || currentUser.role === 'super_admin';
  const canUsePendingApprovedTabs =
    currentUser.role === 'super_admin' || currentUser.role === 'owner' || currentUser.role === 'manager';
      const isStaffOnly = currentUser.role === 'staff';
  const [view, setView] = useState<ApprovalView>(currentUser.role === 'owner' || currentUser.role === 'super_admin' ? 'needs_my_approval' : 'my_requests');
  const [approvalTab, setApprovalTab] = useState<'pending' | 'approved'>('pending');
  const [approvedDateFilter, setApprovedDateFilter] = useState<ApprovedDateFilter>('today');
  const [approvedFromDate, setApprovedFromDate] = useState('');
  const [approvedToDate, setApprovedToDate] = useState('');
  const [showApprovedDateFilterMenu, setShowApprovedDateFilterMenu] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
  // Set default month filter to current month
  const getCurrentMonth = () => {
    const now = new Date();
    return now.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };
  const [monthFilter, setMonthFilter] = useState<string>(getCurrentMonth());
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  // Date range filter state
  const [dateRangeFilter, setDateRangeFilter] = useState<'all' | 'custom'>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const syncInputOnPickerClose = (input: HTMLInputElement | null, setter: (value: string) => void) => {
    if (!input) return;
    const tick = () => {
      if (document.activeElement === input) {
        requestAnimationFrame(tick);
      } else {
        setter(input.value);
      }
    };
    requestAnimationFrame(tick);
  };
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);
  const [approvers, setApprovers] = useState<ApproverOption[]>([]);
  const [selectedEscalationAdminByApproval, setSelectedEscalationAdminByApproval] = useState<Record<string, string>>({});
  const superAdmins = approvers.filter(a => a.role === 'super_admin' || a.role === 'owner');
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [creatingApproval, setCreatingApproval] = useState(false);
  const [requestTitle, setRequestTitle] = useState('');
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [requestDescription, setRequestDescription] = useState('');
  const [requestAmount, setRequestAmount] = useState('');
  const [requestApproverId, setRequestApproverId] = useState('');
  const [requestAttachments, setRequestAttachments] = useState<PendingAttachment[]>([]);
  const [threads, setThreads] = useState<ApprovalThreadView[]>([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [companyEmployees, setCompanyEmployees] = useState<Employee[]>([]);
  const [approvalReadAtById, setApprovalReadAtById] = useState<Record<string, number>>({});
  const [approvalUnreadCountById, setApprovalUnreadCountById] = useState<Record<string, number>>({});
  const [approverScopeIds, setApproverScopeIds] = useState<string[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [processingApprovalId, setProcessingApprovalId] = useState<string | null>(null);
  const [processingApprovalAction, setProcessingApprovalAction] = useState<
    | 'approve'
    | 'reject'
    | 'escalate'
    | 'admin_approve'
    | 'admin_reject'
    | 'final_approve'
    | 'review'
    | null
  >(null);
  const [draftMessage, setDraftMessage] = useState('');
  const [mentionCandidates, setMentionCandidates] = useState<MentionCandidate[]>([]);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState<MentionCandidate[]>([]);
  const [selectedChatMedia, setSelectedChatMedia] = useState<PendingApprovalChatMedia | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState<ApprovalThreadView | null>(null);
  const [editingMessage, setEditingMessage] = useState<ApprovalThreadView | null>(null);
  const [reactionsByMessageId, setReactionsByMessageId] = useState<Record<string, MessageReaction[]>>({});
  const [reactionNameOverridesByUserId, setReactionNameOverridesByUserId] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const attachmentsInputRef = useRef<HTMLInputElement | null>(null);
  const approvalChatPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const approvalChatDocumentInputRef = useRef<HTMLInputElement | null>(null);
  const approvalChatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const approvedDateFilterRef = useRef<HTMLDivElement | null>(null);
  const attachmentFilesRef = useRef<Record<string, File>>({});
  const approvalChatReadsTableMissingRef = useRef(false);
  const approvalThreadRichColumnsMissingRef = useRef(false);
  const pendingDeepLinkApprovalIdRef = useRef<string | null>(null);
  const approvalChatTouchGestureRef = useRef<{ message: ApprovalThreadView; startX: number; startY: number; replyTriggered: boolean } | null>(null);
  const currentUserAuthId = useMemo(() => String(currentUser.auth_user_id || '').trim(), [currentUser.auth_user_id]);
  const currentUserIdentitySet = useMemo(() => {
    const ids = new Set<string>();
    const employeeId = String(currentUser.id || '').trim();
    if (employeeId) ids.add(employeeId);
    if (currentUserAuthId) ids.add(currentUserAuthId);
    return ids;
  }, [currentUser.id, currentUserAuthId]);
  const currentApproverIdSet = useMemo(() => {
    const ids = new Set(currentUserIdentitySet);
    approverScopeIds.forEach((id) => {
      const normalizedId = String(id || '').trim();
      if (normalizedId) ids.add(normalizedId);
    });
    return ids;
  }, [approverScopeIds, currentUserIdentitySet]);
  const isApprovalForCurrentApprover = useCallback(
    (approval: Pick<ApprovalItem, 'approver_id' | 'isEscalated' | 'escalated_to'>): boolean => {
      const directApproverId = String(approval.approver_id || '').trim();
      const escalatedApproverId = String(approval.escalated_to || '').trim();
      const directApproval = !!directApproverId && currentApproverIdSet.has(directApproverId);
      const escalatedApproval =
        isAdminApprover &&
        approval.isEscalated &&
        !!escalatedApproverId &&
        currentApproverIdSet.has(escalatedApproverId);
      return directApproval || escalatedApproval;
    },
    [currentApproverIdSet, isAdminApprover]
  );
  const isCurrentUserSenderId = useCallback(
    (senderId: unknown): boolean => {
      const normalizedSenderId = String(senderId || '').trim();
      return !!normalizedSenderId && currentUserIdentitySet.has(normalizedSenderId);
    },
    [currentUserIdentitySet]
  );
  const getAuthenticatedUserId = useCallback(async (): Promise<string | null> => {
    const { data, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.warn('Failed to resolve authenticated user before posting approval thread message:', authError);
      logSupabaseQueryError(authError);
      return null;
    }
    const authUserId = String(data?.user?.id || '').trim();
    return authUserId || null;
  }, []);
  const approvalReadStorageKey = useMemo(() => `approval-chat-read:${currentUser.id}`, [currentUser.id]);
  const [employeeNamesById, setEmployeeNamesById] = useState<Record<string, string>>({});
  const mentionableMembers = useMemo<MentionCandidate[]>(
    () =>
      companyEmployees
        .filter((employee) => employee?.id && employee.id !== currentUser.id)
        .map((employee) => ({
          id: employee.id,
          name: String(employee.name || '').trim() || 'User',
        })),
    [companyEmployees, currentUser.id]
  );

  const selectedApproval = useMemo(
    () => approvals.find((item) => item.id === selectedApprovalId) || null,
    [approvals, selectedApprovalId]
  );
  const [viewingTaskApprovalId, setViewingTaskApprovalId] = useState<string | null>(null);
  const selectedApprovalTask = useMemo(
    () => approvals.find((item) => item.id === viewingTaskApprovalId)?.linkedTask || null,
    [approvals, viewingTaskApprovalId]
  );
  const selectedApprovalParentTask = useMemo(() => {
    if (!selectedApprovalTask?.parentTaskId) {
      return undefined;
    }
    return approvals.find((item) => item.linkedTask?.id === selectedApprovalTask.parentTaskId)?.linkedTask;
  }, [approvals, selectedApprovalTask]);
  const selectedApprovalSubTasks = useMemo(
    () =>
      selectedApprovalTask
        ? approvals
            .map((item) => item.linkedTask)
            .filter((task): task is DealershipTask => Boolean(task) && task.parentTaskId === selectedApprovalTask.id)
        : [],
    [approvals, selectedApprovalTask]
  );

  const dayStartMs = useCallback((date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime(), []);
  const nowMs = Date.now();
  const todayStartMs = dayStartMs(new Date(nowMs));
  const yesterdayStartMs = todayStartMs - 24 * 60 * 60 * 1000;
  const last7StartMs = todayStartMs - 6 * 24 * 60 * 60 * 1000;
  const approvedFromStartMs = approvedFromDate
    ? dayStartMs(new Date(`${approvedFromDate}T00:00:00`))
    : null;
  const approvedToEndMs = approvedToDate
    ? dayStartMs(new Date(`${approvedToDate}T00:00:00`)) + (24 * 60 * 60 * 1000) - 1
    : null;

  const scopedApprovalsForTabs = useMemo(() => {
    const base = view === 'my_requests'
      ? approvals
      : approvals.filter(isApprovalForCurrentApprover);
    return sortApprovalsByRecency(base);
  }, [approvals, isApprovalForCurrentApprover, view]);

  const pendingApprovalCount = useMemo(
    () => scopedApprovalsForTabs.filter((item) => isPendingApprovalStatus(item.status)).length,
    [scopedApprovalsForTabs]
  );

  const approvedTodayCount = useMemo(
    () =>
      scopedApprovalsForTabs.filter((item) => {
        if (!isApprovedApprovalStatus(item.status)) return false;
        const updatedMs = parseDateTimeMs(getApprovalUpdatedAtValue(item));
        return updatedMs >= todayStartMs && updatedMs <= nowMs;
      }).length,
    [scopedApprovalsForTabs, todayStartMs, nowMs]
  );

  const handleOpenPendingTab = () => {
    setApprovalTab('pending');
    setShowApprovedDateFilterMenu(false);
  };

  const handleOpenApprovedTab = () => {
    setApprovalTab('approved');
    setApprovedDateFilter('today');
    setApprovedFromDate('');
    setApprovedToDate('');
    setShowApprovedDateFilterMenu(false);
  };

  const approvedFilterLabel =
    approvedDateFilter === 'today'
      ? 'Today'
      : approvedDateFilter === 'yesterday'
      ? 'Yesterday'
      : approvedDateFilter === 'last7'
      ? 'Last 7 days'
      : 'Custom range';

  const isDecisionWithinApprovedFilter = useCallback(
    (approval: ApprovalItem): boolean => {
      const updatedMs = parseDateTimeMs(getApprovalUpdatedAtValue(approval));
      if (!updatedMs) return false;
      if (approvedDateFilter === 'today') {
        return updatedMs >= todayStartMs && updatedMs <= nowMs;
      }
      if (approvedDateFilter === 'yesterday') {
        return updatedMs >= yesterdayStartMs && updatedMs < todayStartMs;
      }
      if (approvedDateFilter === 'last7') {
        return updatedMs >= last7StartMs && updatedMs <= nowMs;
      }
      if (approvedDateFilter === 'custom') {
        const from = approvedFromStartMs ?? Number.MIN_SAFE_INTEGER;
        const to = approvedToEndMs ?? Number.MAX_SAFE_INTEGER;
        return updatedMs >= from && updatedMs <= to;
      }
      return true;
    },
    [
      approvedDateFilter,
      approvedFromStartMs,
      approvedToEndMs,
      last7StartMs,
      nowMs,
      todayStartMs,
      yesterdayStartMs,
    ]
  );

  const filterNeedsMyApprovalQueue = useCallback(
    (items: ApprovalItem[]): ApprovalItem[] =>
      items.filter(isApprovalForCurrentApprover),
    [isApprovalForCurrentApprover]
  );

  const withTimeout = useCallback(<T,>(promise: Promise<T>, ms: number): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error('Operation timed out')), ms)
      ),
    ]);
  }, []);

  const runApprovalAction = useCallback(
    async (approvalId: string, action: NonNullable<typeof processingApprovalAction>, fn: () => Promise<void>) => {
      if (!approvalId || processingApprovalId) return;
      setProcessingApprovalId(approvalId);
      setProcessingApprovalAction(action);
      try {
        await withTimeout(fn(), 30000);
      } catch (err) {
        const message = getActionErrorMessage(err);
        setError(message);
      } finally {
        setProcessingApprovalId(null);
        setProcessingApprovalAction(null);
      }
    },
    [processingApprovalId, withTimeout]
  );

  const getLocalApprovalReadMap = useCallback((): Record<string, number> => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = localStorage.getItem(approvalReadStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const map: Record<string, number> = {};
      Object.entries(parsed).forEach(([approvalId, value]) => {
        const ts = parseDateTimeMs(value);
        if (approvalId && ts > 0) {
          map[approvalId] = ts;
        }
      });
      return map;
    } catch {
      return {};
    }
  }, [approvalReadStorageKey]);

  const saveLocalApprovalReadMap = useCallback((next: Record<string, number>) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(approvalReadStorageKey, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  }, [approvalReadStorageKey]);

  useEffect(() => {
    setApprovalReadAtById(getLocalApprovalReadMap());
  }, [getLocalApprovalReadMap]);

  const loadApprovalReadReceipts = useCallback(async (approvalIds: string[]) => {
    if (!approvalIds.length || approvalChatReadsTableMissingRef.current) {
      return;
    }

    const { data, error: readError } = await supabase
      .from('approval_chat_reads')
      .select('approval_id, last_read_at')
      .eq('user_id', currentUser.id)
      .in('approval_id', approvalIds);

    if (readError) {
      const msg = String(readError.message || '').toLowerCase();
      const missingTable =
        (msg.includes('relation') && msg.includes('approval_chat_reads')) ||
        (msg.includes('does not exist') && msg.includes('approval_chat_reads'));
      if (missingTable) {
        approvalChatReadsTableMissingRef.current = true;
        console.warn('approval_chat_reads table is missing; unread approval badges are disabled until migration is run.');
        return;
      }
      console.warn('Failed to load approval chat read state:', readError);
      logSupabaseQueryError(readError);
      return;
    }

    const localMap = getLocalApprovalReadMap();
    const next: Record<string, number> = { ...localMap };
    for (const row of data || []) {
      const approvalId = String((row as any).approval_id || '');
      if (!approvalId) continue;
      next[approvalId] = Math.max(next[approvalId] || 0, parseDateTimeMs((row as any).last_read_at));
    }
    setApprovalReadAtById((prev) => ({ ...prev, ...next }));
    saveLocalApprovalReadMap(next);
  }, [currentUser.id, getLocalApprovalReadMap, saveLocalApprovalReadMap]);

  const recomputeApprovalUnreadCounts = useCallback(async (
    approvalIds: string[],
    readMapOverride?: Record<string, number>
  ) => {
    if (!approvalIds.length) {
      setApprovalUnreadCountById({});
      return;
    }

    const { data, error: threadsError } = await supabase
      .from('approval_threads')
      .select('approval_id, sender_id, created_at')
      .in('approval_id', approvalIds);

    if (threadsError) {
      console.warn('Failed to compute approval unread counts:', threadsError);
      logSupabaseQueryError(threadsError);
      return;
    }

    const readMap = readMapOverride ?? approvalReadAtById;
    const counts: Record<string, number> = {};
    for (const approvalId of approvalIds) {
      counts[approvalId] = 0;
    }

    for (const row of data || []) {
      const approvalId = String((row as any).approval_id || '');
      if (!approvalId || !(approvalId in counts)) continue;
      const senderId = String((row as any).sender_id || '');
      if (isCurrentUserSenderId(senderId)) continue;
      const createdAtMs = parseDateTimeMs((row as any).created_at);
      const lastReadAtMs = readMap[approvalId] || 0;
      if (createdAtMs > lastReadAtMs) {
        counts[approvalId] += 1;
      }
    }

    setApprovalUnreadCountById(counts);
  }, [approvalReadAtById, isCurrentUserSenderId]);

  const markApprovalThreadRead = useCallback(async (approvalId: string, readAtMs?: number) => {
    if (!approvalId || approvalChatReadsTableMissingRef.current) return;

    let resolvedReadAtMs = readAtMs || 0;
    if (!resolvedReadAtMs) {
      const { data: latestRow, error: latestError } = await supabase
        .from('approval_threads')
        .select('created_at')
        .eq('approval_id', approvalId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (latestError) {
        console.warn('Failed to resolve latest approval thread timestamp:', latestError);
        logSupabaseQueryError(latestError);
      } else if (latestRow?.created_at) {
        const latestTs = parseDateTimeMs(latestRow.created_at);
        if (latestTs > 0) {
          resolvedReadAtMs = latestTs + 1;
        }
      }
    }

    const effectiveReadAtMs = Math.max(resolvedReadAtMs, Date.now());
    const readAtIso = new Date(effectiveReadAtMs).toISOString();
    setApprovalReadAtById((prev) => {
      const merged = { ...prev, [approvalId]: Math.max(prev[approvalId] || 0, effectiveReadAtMs) };
      saveLocalApprovalReadMap(merged);
      return merged;
    });
    setApprovalUnreadCountById((prev) => ({ ...prev, [approvalId]: 0 }));

    const { error: upsertError } = await supabase
      .from('approval_chat_reads')
      .upsert(
        {
          approval_id: approvalId,
          user_id: currentUser.id,
          last_read_at: readAtIso,
        },
        { onConflict: 'approval_id,user_id' }
      );

    if (upsertError) {
      const msg = String(upsertError.message || '').toLowerCase();
      const missingTable =
        (msg.includes('relation') && msg.includes('approval_chat_reads')) ||
        (msg.includes('does not exist') && msg.includes('approval_chat_reads'));
      if (missingTable) {
        approvalChatReadsTableMissingRef.current = true;
        console.warn('approval_chat_reads table is missing; unread approval badges are disabled until migration is run.');
        return;
      }
      console.warn('Failed to mark approval chat as read:', upsertError);
      logSupabaseQueryError(upsertError);
    }
  }, [currentUser.id, saveLocalApprovalReadMap]);

  const toggleApprovalSelection = useCallback((approvalId: string) => {
    setSelectedApprovalId((prev) => {
      const next = prev === approvalId ? null : approvalId;
      if (next) {
        void markApprovalThreadRead(next);
      }
      return next;
    });
  }, [markApprovalThreadRead]);

  useEffect(() => {
    const deepLinkApprovalId = String(deepLinkApprovalTarget?.approvalId || '').trim();
    if (!deepLinkApprovalId) {
      return;
    }

    pendingDeepLinkApprovalIdRef.current = deepLinkApprovalId;
    if (isAdminApprover) {
      setView('needs_my_approval');
    }
  }, [deepLinkApprovalTarget?.approvalId, deepLinkApprovalTarget?.token, isAdminApprover]);

  useEffect(() => {
    const pendingApprovalId = String(pendingDeepLinkApprovalIdRef.current || '').trim();
    if (!pendingApprovalId || !approvals.length) {
      return;
    }

    if (!approvals.some((approval) => approval.id === pendingApprovalId)) {
      return;
    }

    pendingDeepLinkApprovalIdRef.current = null;
    setSelectedApprovalId(pendingApprovalId);
    void markApprovalThreadRead(pendingApprovalId);
    onDeepLinkApprovalHandled?.();
  }, [approvals, markApprovalThreadRead, onDeepLinkApprovalHandled]);

  const loadApprovers = useCallback(async () => {
    setLoadingApprovers(true);
      try {
        const { data, error: approverError } = await supabase
          .from('employees')
          .select('id, name, mobile, role, company_id, auth_user_id')
          .eq('company_id', currentUser.company_id);
        if (approverError) throw approverError;

      const directory = (data || []).map((row: any) => ({
        id: String(row.id || ''),
        name: String(row.name || 'Unknown'),
        email: '',
        role: String(row.role || 'staff') as Employee['role'],
        mobile: String(row.mobile || ''),
        company_id: String(row.company_id || currentUser.company_id),
        auth_user_id: row.auth_user_id ? String(row.auth_user_id) : undefined,
      })).filter((item) => item.id);

      const candidates = directory
        .map((row) => ({
          id: row.id,
          name: row.name,
          role: row.role,
        }))
        .filter((item) => item.id && item.role !== 'staff')
        .sort((a, b) => a.name.localeCompare(b.name));

      const names = directory.reduce<Record<string, string>>((acc, row) => {
        const id = String(row.id || '');
        if (!id) return acc;
        acc[id] = String(row.name || 'Unknown');
        if (row.auth_user_id) {
          acc[String(row.auth_user_id)] = String(row.name || 'Unknown');
        }
        return acc;
      }, {});

      setCompanyEmployees(directory);
      setApprovers(candidates);
      setEmployeeNamesById(names);
      if (!requestApproverId && candidates.length) {
        setRequestApproverId(candidates[0].id);
      }
    } catch (loadApproverErr: any) {
      setError(loadApproverErr?.message || 'Failed to load approvers.');
    } finally {
      setLoadingApprovers(false);
    }
  }, [currentUser.company_id, requestApproverId]);

  const resolveCurrentApproverIds = useCallback(async (): Promise<string[]> => {
    const ids = new Set<string>();
    currentUserIdentitySet.forEach((id) => ids.add(id));

    if (currentUserAuthId) {
      const { data, error: employeeError } = await supabase
        .from('employees')
        .select('id')
        .eq('auth_user_id', currentUserAuthId);

      if (employeeError) {
        console.warn('Failed to load cross-company approver identities:', employeeError);
        logSupabaseQueryError(employeeError);
      } else {
        (data || []).forEach((row: any) => {
          const id = String(row?.id || '').trim();
          if (id) ids.add(id);
        });
      }
    }

    const resolvedIds = Array.from(ids);
    setApproverScopeIds((prev) => {
      const prevKey = [...prev].sort().join('|');
      const nextKey = [...resolvedIds].sort().join('|');
      return prevKey === nextKey ? prev : resolvedIds;
    });
    return resolvedIds;
  }, [currentUserAuthId, currentUserIdentitySet]);

  const loadLinkedTasksByApprovalId = useCallback(async (items: ApprovalItem[]): Promise<Record<string, DealershipTask>> => {
    const taskIds = Array.from(
      new Set(
        items
          .map((item) => String(item.task_id || '').trim())
          .filter(Boolean)
      )
    );

    if (!taskIds.length) {
      return {};
    }

    const { data, error: taskError } = await supabase
      .from('tasks')
      .select('*')
      .in('id', taskIds);

    if (taskError) {
      console.error('Failed to load linked approval tasks:', taskError);
      logSupabaseQueryError(taskError);
      return {};
    }

    const tasksById = new Map<string, DealershipTask>();
    for (const row of (data || []) as DatabaseTask[]) {
      const task = transformTaskToApp(row);
      tasksById.set(task.id, task);
    }

    return items.reduce<Record<string, DealershipTask>>((acc, item) => {
      const taskId = String(item.task_id || '').trim();
      if (!taskId) {
        return acc;
      }
      const linkedTask = tasksById.get(taskId);
      if (linkedTask) {
        acc[item.id] = linkedTask;
      }
      return acc;
    }, {});
  }, []);

  const loadApprovals = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (!silent) {
      setLoadingApprovals(true);
      setError(null);
    }
    try {
      let query = supabase
        .from('approvals')
        .select('id, requester_id, approver_id, title, description, amount, status, task_id, isEscalated, adminEscalationStatus, escalated_to, created_at, updated_at')
        .eq('company_id', currentUser.company_id);

      let rows: any[] = [];
      let resolvedApproverIdSet: Set<string> | null = null;

      if (view === 'my_requests') {
        const { data: requesterData, error: requesterError } = await query
          .eq('requester_id', currentUser.id)
          .order('id', { ascending: false });
        if (requesterError) throw requesterError;
        rows = requesterData || [];
      } else {
        const approverIds = await resolveCurrentApproverIds();
        resolvedApproverIdSet = new Set(approverIds);
        if (isAdminApprover) {
          const encodedIds = approverIds.map((id) => id.replace(/[(),]/g, '')).join(',');
          query = encodedIds
            ? query.or(`approver_id.in.(${encodedIds}),escalated_to.in.(${encodedIds})`)
            : query.eq('approver_id', currentUser.id);
        } else {
          // Regular Managers only see requests directly assigned to them
          query = approverIds.length
            ? query.in('approver_id', approverIds)
            : query.eq('approver_id', currentUser.id);
        }

        const { data: approverData, error: approverError } = await query.order('id', { ascending: false });
        if (approverError) throw approverError;
        rows = approverData || [];
      }

      const mappedRowsBase: ApprovalItem[] = (rows || []).map((row: any) => ({
        id: String(row.id),
        requester_id: String(row.requester_id || ''),
        approver_id: String(row.approver_id || ''),
        title: String(row.title || ''),
        description: String(row.description || ''),
        amount: row.amount === null || row.amount === undefined ? null : Number(row.amount),
        status: normalizeStatus(row.status),
        company_id: row.company_id ? String(row.company_id) : null,
        task_id: row.task_id ? String(row.task_id) : null,
        created_at: row.created_at ? String(row.created_at) : null,
        updated_at: row.updated_at ? String(row.updated_at) : null,
        isEscalated: Boolean(row.isEscalated || false),
        adminEscalationStatus: String(row.adminEscalationStatus || 'NONE'),
        escalated_to: row.escalated_to ? String(row.escalated_to) : null,
        linkedTask: null,
      }));

	      const linkedTasksByApprovalId = await loadLinkedTasksByApprovalId(mappedRowsBase);
	      let mappedRows = mappedRowsBase.map((item) => ({
	        ...item,
	        linkedTask: linkedTasksByApprovalId[item.id] || null,
	      }));

	      const isStaleTaskLinkedApproval = (item: ApprovalItem): boolean =>
	        (
	          isTaskExtensionApproval(item) &&
	          isPendingApprovalStatus(item.status) &&
	          isExtensionClosedForLinkedTask(item)
	        ) ||
	        (
	          isHighPriorityTaskCompletionApproval(item) &&
	          isPendingApprovalStatus(item.status) &&
	          isHighPriorityCompletionStaleForLinkedTask(item)
	        );

	      const staleExtensionApprovals = mappedRows.filter(
	        (item) =>
	          isTaskExtensionApproval(item) &&
	          isPendingApprovalStatus(item.status) &&
	          isExtensionClosedForLinkedTask(item)
	      );
	      const staleHighPriorityCompletionApprovals = mappedRows.filter(
	        (item) =>
	          isHighPriorityTaskCompletionApproval(item) &&
	          isPendingApprovalStatus(item.status) &&
	          isHighPriorityCompletionStaleForLinkedTask(item)
	      );
	      const staleTaskLinkedApprovals = mappedRows.filter(isStaleTaskLinkedApproval);

	      if (staleTaskLinkedApprovals.length > 0) {
	        const staleApprovalIds = staleTaskLinkedApprovals.map((item) => item.id);
	        const staleExtensionTaskIds = Array.from(
	          new Set(
	            staleExtensionApprovals
	              .map((item) => String(item.task_id || '').trim())
	              .filter(Boolean)
	          )
	        );
	        const staleUpdatedAt = new Date().toISOString();

	        const { error: staleApprovalError } = await supabase
	          .from('approvals')
	          .update({ status: 'REJECTED' })
	          .in('id', staleApprovalIds);

	        if (staleApprovalError) {
	          console.warn('Failed to close stale task-linked approvals:', staleApprovalError);
	          logSupabaseQueryError(staleApprovalError);
	        } else {
	          if (staleExtensionTaskIds.length > 0) {
	            const { error: staleTaskError } = await supabase
	              .from('tasks')
	              .update({ extension_status: 'REJECTED' })
	              .in('id', staleExtensionTaskIds);

	            if (staleTaskError) {
	              console.warn('Failed to mark stale completed-task extension fields rejected:', staleTaskError);
	              logSupabaseQueryError(staleTaskError);
	            }
	          }
	        }

	        mappedRows = mappedRows.map((item) =>
	          staleApprovalIds.includes(item.id)
	            ? {
	                ...item,
	                status: 'REJECTED',
	                updated_at: staleUpdatedAt,
	                linkedTask: item.linkedTask
	                  ? { ...item.linkedTask, extensionStatus: 'REJECTED' }
	                  : item.linkedTask,
	              }
	            : item
	        );
	      }

	      const mapped = sortApprovalsByRecency(
        view === 'needs_my_approval'
          ? mappedRows.filter((approval) => {
              const approverIdsForLoad = resolvedApproverIdSet ?? currentApproverIdSet;
              const directApproverId = String(approval.approver_id || '').trim();
              const escalatedApproverId = String(approval.escalated_to || '').trim();
              const directApproval = !!directApproverId && approverIdsForLoad.has(directApproverId);
              const escalatedApproval =
                isAdminApprover &&
                approval.isEscalated &&
                !!escalatedApproverId &&
                approverIdsForLoad.has(escalatedApproverId);
              return directApproval || escalatedApproval;
            })
          : mappedRows
      );

      setApprovals((prev) => (approvalsAreEqual(prev, mapped) ? prev : mapped));

      if (!mapped.length) {
        setSelectedApprovalId(null);
        setThreads([]);
        return;
      }

      setSelectedApprovalId((prev) => {
        // Keep manual collapse state (null) across refresh/realtime updates.
        if (!prev) {
          return null;
        }
        return mapped.some((a) => a.id === prev) ? prev : null;
      });
    } catch (loadErr: any) {
      if (!silent) {
        setError(loadErr?.message || 'Failed to load approvals.');
      }
    } finally {
      if (!silent) {
        setLoadingApprovals(false);
      }
    }
  }, [currentApproverIdSet, currentUser.id, currentUser.role, filterNeedsMyApprovalQueue, isAdminApprover, loadLinkedTasksByApprovalId, resolveCurrentApproverIds, view]);

  const loadThreads = useCallback(async (approvalId: string, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (!silent) {
      setLoadingThreads(true);
    }
    try {
      const richThreadFields =
        'id, approval_id, sender_id, message_text, created_at, reply_to_id, media_url, media_type, media_name, media_size, is_edited, edited_at';
      const basicThreadFields = 'id, approval_id, sender_id, message_text, created_at';
      let threadQuery = supabase
        .from('approval_threads')
        .select(approvalThreadRichColumnsMissingRef.current ? basicThreadFields : richThreadFields)
        .eq('approval_id', approvalId)
        .order('created_at', { ascending: true });
      let { data, error: threadError } = await threadQuery;
      if (threadError && isMissingThreadColumnError(threadError)) {
        approvalThreadRichColumnsMissingRef.current = true;
        const retry = await supabase
          .from('approval_threads')
          .select(basicThreadFields)
          .eq('approval_id', approvalId)
          .order('created_at', { ascending: true });
        data = retry.data;
        threadError = retry.error;
      }
      if (threadError) throw threadError;

      const rows: ApprovalThread[] = (data || []).map((row: any) => ({
        id: String(row.id),
        approval_id: String(row.approval_id || approvalId),
        sender_id: String(row.sender_id || ''),
        message_text: String(row.message_text || ''),
        created_at: String(row.created_at || new Date().toISOString()),
        reply_to_id: row.reply_to_id ? String(row.reply_to_id) : null,
        media_url: row.media_url ? String(row.media_url) : null,
        media_type: row.media_type || null,
        media_name: row.media_name ? String(row.media_name) : null,
        media_size: row.media_size === null || row.media_size === undefined ? null : Number(row.media_size),
        is_edited: Boolean(row.is_edited),
        edited_at: row.edited_at ? String(row.edited_at) : null,
      }));

      const senderIds = Array.from(new Set(rows.map((row) => row.sender_id).filter(Boolean)));
      let senderMap: Record<string, string> = {};

      if (senderIds.length) {
        let { data: senders, error: senderError } = await supabase
          .from('employees')
          .select('id, name, auth_user_id')
          .or(`id.in.(${senderIds.join(',')}),auth_user_id.in.(${senderIds.join(',')})`);
        if (senderError && isMissingThreadColumnError(senderError)) {
          const retry = await supabase
            .from('employees')
            .select('id, name')
            .in('id', senderIds);
          senders = retry.data;
          senderError = retry.error;
        }
        if (senderError) throw senderError;

        senderMap = (senders || []).reduce<Record<string, string>>((acc, sender: any) => {
          const name = String(sender.name || 'Unknown');
          acc[String(sender.id)] = name;
          if (sender.auth_user_id) {
            acc[String(sender.auth_user_id)] = name;
          }
          return acc;
        }, {});
      }

      const baseRows = rows.map((row) => ({
        ...row,
        created_at_ms: parseDateTimeMs(row.created_at) || Date.now(),
        sender_name: senderMap[row.sender_id] || (isCurrentUserSenderId(row.sender_id) ? (currentUser.name || 'You') : 'Unknown'),
      })).sort((left, right) => left.created_at_ms - right.created_at_ms);
      const byId = new Map(baseRows.map((row) => [row.id, row]));
      const normalized = baseRows.map((row) => {
        const replyTarget = row.reply_to_id ? byId.get(String(row.reply_to_id)) : null;
        return {
          ...row,
          reply_to: replyTarget
            ? {
                id: replyTarget.id,
                sender_name: replyTarget.sender_name,
                message_text: replyTarget.message_text,
                media_type: replyTarget.media_type || null,
                media_name: replyTarget.media_name || null,
              }
            : null,
        };
      });

      setThreads((prev) => (threadsAreEqual(prev, normalized) ? prev : normalized));
    } catch (threadErr: any) {
      if (!silent) {
        setError(threadErr?.message || 'Failed to load discussion thread.');
      }
    } finally {
      if (!silent) {
        setLoadingThreads(false);
      }
    }
  }, [currentUser.name, isCurrentUserSenderId]);

  const loadApprovalThreadReactions = useCallback(async (messageIds: string[]) => {
    const normalizedIds = messageIds.map((id) => String(id || '').trim()).filter((id) => id && !id.startsWith('temp-'));
    if (!normalizedIds.length) {
      setReactionsByMessageId({});
      return;
    }

    const { data, error: reactionError } = await supabase
      .from('message_reactions')
      .select('id, message_id, user_id, emoji')
      .in('message_id', normalizedIds);

    if (reactionError) {
      console.warn('Failed to load approval chat reactions:', reactionError);
      logSupabaseQueryError(reactionError);
      return;
    }

    const grouped = (data || []).reduce<Record<string, MessageReaction[]>>((acc, row: any) => {
      const messageId = String(row.message_id || '').trim();
      if (!messageId) return acc;
      const reaction: MessageReaction = {
        id: String(row.id || `${messageId}:${row.user_id || ''}`),
        message_id: messageId,
        user_id: String(row.user_id || ''),
        emoji: String(row.emoji || '👍'),
      };
      acc[messageId] = [...(acc[messageId] || []), reaction];
      return acc;
    }, {});

    setReactionsByMessageId(grouped);
  }, []);

  const getApprovalReactionDisplayNames = useCallback((messageId: string): string[] => {
    const seen = new Set<string>();
    return (reactionsByMessageId[messageId] || [])
      .filter((reaction) => reaction.emoji === '👍')
      .map((reaction) => {
        const userId = String(reaction.user_id || '').trim();
        if (!userId || seen.has(userId)) {
          return '';
        }
        seen.add(userId);
        if (currentUserIdentitySet.has(userId)) {
          return String(currentUser.name || '').trim() || 'You';
        }
        return reactionNameOverridesByUserId[userId] || employeeNamesById[userId] || 'Unknown User';
      })
      .filter(Boolean);
  }, [
    currentUser.name,
    currentUserIdentitySet,
    employeeNamesById,
    reactionNameOverridesByUserId,
    reactionsByMessageId,
  ]);

  const fetchApprovalReactionNames = useCallback(async (messageId: string) => {
    const normalizedMessageId = String(messageId || '').trim();
    if (!normalizedMessageId) return;

    const { data: reactionRows, error: reactionError } = await supabase
      .from('message_reactions')
      .select('user_id')
      .eq('message_id', normalizedMessageId);

    if (reactionError) {
      console.warn('Failed to load approval reaction users:', reactionError);
      logSupabaseQueryError(reactionError);
      return;
    }

    const unknownUserIds = Array.from(
      new Set(
        (reactionRows || [])
          .map((row: any) => String(row?.user_id || '').trim())
          .filter((userId) =>
            userId &&
            !currentUserIdentitySet.has(userId) &&
            !employeeNamesById[userId] &&
            !reactionNameOverridesByUserId[userId]
          )
      )
    );

    if (!unknownUserIds.length) {
      return;
    }

    const safeIdCsv = unknownUserIds.map((id) => id.replace(/[(),]/g, '')).join(',');
    const { data: employeeRows, error: employeeError } = await supabase
      .from('employees')
      .select('id, name, auth_user_id')
      .eq('company_id', currentUser.company_id)
      .or(`id.in.(${safeIdCsv}),auth_user_id.in.(${safeIdCsv})`);

    if (employeeError) {
      console.warn('Failed to resolve approval reaction names:', employeeError);
      logSupabaseQueryError(employeeError);
      return;
    }

    const nextNames: Record<string, string> = {};
    (employeeRows || []).forEach((employee: any) => {
      const employeeId = String(employee?.id || '').trim();
      const authUserId = String(employee?.auth_user_id || '').trim();
      const name = String(employee?.name || '').trim();
      if (!name) return;
      if (employeeId) nextNames[employeeId] = name;
      if (authUserId) nextNames[authUserId] = name;
    });

    if (Object.keys(nextNames).length > 0) {
      setReactionNameOverridesByUserId((prev) => ({ ...prev, ...nextNames }));
    }
  }, [
    currentUser.company_id,
    currentUserIdentitySet,
    employeeNamesById,
    reactionNameOverridesByUserId,
  ]);

  useEffect(() => {
    void loadApprovals();
  }, [loadApprovals]);

  useEffect(() => {
    void loadApprovers();
  }, [loadApprovers]);

  useEffect(() => {
    if (!isStaffOnly) return;
    setView('my_requests');
  }, [isStaffOnly]);

  useEffect(() => {
    if (!showApprovedDateFilterMenu) return;
    const handleOutside = (event: MouseEvent) => {
      if (!approvedDateFilterRef.current) return;
      const target = event.target as Node;
      if (!approvedDateFilterRef.current.contains(target)) {
        setShowApprovedDateFilterMenu(false);
      }
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [showApprovedDateFilterMenu]);

  useEffect(() => {
    if (!selectedApprovalId) return;
    void loadThreads(selectedApprovalId);
    setDraftMessage('');
    setMentionMenuOpen(false);
    setMentionCandidates([]);
    setMentionStartIndex(null);
    setActiveMentionIndex(0);
    setSelectedMentions([]);
    setReplyingToMessage(null);
    setEditingMessage(null);
    setShowAttachmentMenu(false);
    setSelectedChatMedia(null);
  }, [selectedApprovalId, loadThreads]);

  useEffect(() => {
    void loadApprovalThreadReactions(threads.map((thread) => thread.id));
  }, [threads, loadApprovalThreadReactions]);

  useEffect(() => {
    if (!selectedApprovalId) return;
    void markApprovalThreadRead(selectedApprovalId);
  }, [selectedApprovalId, markApprovalThreadRead]);

  useEffect(() => {
    const approvalIds = approvals.map((item) => item.id);
    if (!approvalIds.length) {
      setApprovalUnreadCountById({});
      return;
    }
    void loadApprovalReadReceipts(approvalIds);
  }, [approvals, loadApprovalReadReceipts]);

  useEffect(() => {
    const approvalIds = approvals.map((item) => item.id);
    if (!approvalIds.length) {
      setApprovalUnreadCountById({});
      return;
    }
    void recomputeApprovalUnreadCounts(approvalIds);
  }, [approvals, approvalReadAtById, recomputeApprovalUnreadCounts]);

  useEffect(() => {
    const isRowRelevantToCurrentUser = (row: any): boolean => {
      const requesterId = String(row?.requester_id || '');
      const approverId = String(row?.approver_id || '');
      const isEscalated = Boolean(row?.isEscalated);
      return requesterId === currentUser.id || approverId === currentUser.id ||
        (isAdminApprover && isEscalated && String(row?.escalated_to || '') === currentUser.id);
    };
    const refreshUnreadCounts = () => {
      const approvalIds = approvals.map((item) => item.id);
      if (!approvalIds.length) return;
      void recomputeApprovalUnreadCounts(approvalIds);
    };

    const channel = supabase
      .channel(`approvals-live-${currentUser.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'approvals' },
        (payload: any) => {
          const newRow = payload?.new || {};
          const oldRow = payload?.old || {};
          const newId = String(newRow?.id || '');
          const oldId = String(oldRow?.id || '');
          const selectedChanged =
            !!selectedApprovalId &&
            (selectedApprovalId === newId || selectedApprovalId === oldId);
          const userRelated =
            isRowRelevantToCurrentUser(newRow) || isRowRelevantToCurrentUser(oldRow);

          if (selectedChanged || userRelated) {
            void loadApprovals({ silent: true });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'approval_threads' },
        (payload: any) => {
          const newApprovalId = String(payload?.new?.approval_id || '');
          const oldApprovalId = String(payload?.old?.approval_id || '');
          const changedApprovalId = newApprovalId || oldApprovalId;
          if (!changedApprovalId) return;

          refreshUnreadCounts();

          const threadForSelectedApproval =
            !!selectedApprovalId &&
            (newApprovalId === selectedApprovalId || oldApprovalId === selectedApprovalId);

          if (!threadForSelectedApproval) return;

          void loadThreads(selectedApprovalId, { silent: true });

          const senderId = String(payload?.new?.sender_id || '');
          if (senderId && !isCurrentUserSenderId(senderId)) {
            const incomingMessageMs = parseDateTimeMs(payload?.new?.created_at);
            void markApprovalThreadRead(selectedApprovalId, incomingMessageMs > 0 ? incomingMessageMs + 1 : undefined);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'message_reactions' },
        (payload: any) => {
          const messageId = String(payload?.new?.message_id || payload?.old?.message_id || '').trim();
          if (!messageId || !threads.some((thread) => thread.id === messageId)) return;
          void loadApprovalThreadReactions(threads.map((thread) => thread.id));
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'approval_chat_reads',
          filter: `user_id=eq.${currentUser.id}`,
        },
        (payload: any) => {
          const approvalId = String(payload?.new?.approval_id || payload?.old?.approval_id || '');
          if (!approvalId) return;
          const readAtMs = parseDateTimeMs(payload?.new?.last_read_at);
          setApprovalReadAtById((prev) => {
            const merged = { ...prev, [approvalId]: readAtMs || Date.now() };
            saveLocalApprovalReadMap(merged);
            return merged;
          });
          if (approvalId === selectedApprovalId) {
            setApprovalUnreadCountById((prev) => ({ ...prev, [approvalId]: 0 }));
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [
    approvals,
    currentUser.id,
    currentUser.role,
    selectedApprovalId,
    loadApprovals,
    loadThreads,
    recomputeApprovalUnreadCounts,
    isCurrentUserSenderId,
    markApprovalThreadRead,
    saveLocalApprovalReadMap,
  ]);

  useEffect(() => {
    const refresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      void loadApprovals({ silent: true });
      if (selectedApprovalId) {
        void loadThreads(selectedApprovalId, { silent: true });
      }
    };

    const intervalId = window.setInterval(refresh, 4000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadApprovals, loadApprovalThreadReactions, loadThreads, selectedApprovalId, threads]);

  const handleCreateRequest = async (): Promise<void> => {
    const title = requestTitle.trim();
    const description = requestDescription.trim();
    const rawAmount = requestAmount.trim();
    const amountValue =
      rawAmount === ''
        ? null
        : Number(rawAmount);
    const approverId = requestApproverId.trim();
    if (creatingApproval) {
      return;
    }

    if (!title) {
      setError('Approval title is required.');
      return;
    }
    if (!approverId) {
      setError('Please choose an approver to tag.');
      return;
    }
    if (amountValue !== null && (!Number.isFinite(amountValue) || amountValue < 0)) {
      setError('Amount must be a valid non-negative number when provided.');
      return;
    }

    setCreatingApproval(true);
    setError(null);

    try {
      let uploadedAttachments: ApprovalAttachment[] = [];
      if (requestAttachments.length > 0) {
        const uploadResults = await Promise.all(
          requestAttachments.map(async (attachment) => {
            const file = attachmentFilesRef.current[attachment.id];
            if (!file) {
              throw new Error(`Attachment "${attachment.name}" is missing. Please re-attach and try again.`);
            }

            const safeName = sanitizeFileName(attachment.name);
            const storagePath = `approval-attachments/${currentUser.company_id}/${currentUser.id}/${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 9)}-${safeName}`;

            const { error: uploadError } = await supabase.storage
              .from(APPROVAL_ATTACHMENT_BUCKET)
              .upload(storagePath, file, {
                contentType: file.type || 'application/octet-stream',
                upsert: false,
              });

            if (uploadError) {
              throw new Error(
                `Failed to upload "${file.name}". Ensure Storage bucket "${APPROVAL_ATTACHMENT_BUCKET}" allows uploads.`
              );
            }

            const { data: publicData } = supabase.storage
              .from(APPROVAL_ATTACHMENT_BUCKET)
              .getPublicUrl(storagePath);

            return {
              name: attachment.name,
              url: String(publicData?.publicUrl || '').trim(),
              contentType: file.type || attachment.contentType || '',
              size: file.size || attachment.size || 0,
            } as ApprovalAttachment;
          })
        );

        uploadedAttachments = uploadResults.filter((item) => Boolean(item.url));
      }

      const descriptionWithAttachments = appendAttachmentsToDescription(description, uploadedAttachments);
      const { data: created, error: createError } = await withTimeout(
        supabase
          .from('approvals')
          .insert({
            requester_id: currentUser.id,
            approver_id: approverId,
            title,
            description: descriptionWithAttachments,
            amount: amountValue,
            status: 'PENDING',
            company_id: currentUser.company_id,
          })
          .select('id, requester_id, approver_id, title, description, amount, status, task_id, created_at, updated_at')
          .single(),
        30000
      );
      if (createError) throw createError;

      const createdApproval: ApprovalItem = {
        id: String(created.id),
        requester_id: String(created.requester_id || currentUser.id),
        approver_id: String(created.approver_id || approverId),
        title: String(created.title || title),
        description: String(created.description || descriptionWithAttachments),
        amount: created.amount === null || created.amount === undefined
          ? amountValue
          : Number(created.amount),
        status: normalizeStatus(created.status),
        company_id: created.company_id ? String(created.company_id) : null,
        task_id: created.task_id ? String(created.task_id) : null,
        created_at: created.created_at ? String(created.created_at) : new Date().toISOString(),
        updated_at: created.updated_at ? String(created.updated_at) : null,
        isEscalated: false,
        adminEscalationStatus: 'NONE',
      };

      setApprovals((prev) => sortApprovalsByRecency([createdApproval, ...prev]));
      setSelectedApprovalId(createdApproval.id);
      setRequestTitle('');
      setRequestDescription('');
      setRequestAmount('');
      setRequestAttachments([]);
      attachmentFilesRef.current = {};
      setView('my_requests');
      setIsApprovalModalOpen(false); // Close modal after creation
      await loadThreads(createdApproval.id);
    } catch (createErr: any) {
      if (createErr instanceof Error && createErr.message === 'Operation timed out') {
        setError('Request timed out. Please check your connection.');
        return;
      }
      setError(createErr?.message || 'Failed to create approval request.');
    } finally {
      setCreatingApproval(false);
    }
  };

  const updateStatus = async (approvalId: string, status: ApprovalStatus): Promise<void> => {
    if (!approvalId) return;
    const targetApproval = approvals.find((item) => item.id === approvalId) || selectedApproval;
    if (!targetApproval) return;
    setUpdatingStatus(true);
    setError(null);
    try {
      console.log('[Approvals] Updating approval status', { approvalId, status });
      const { error: updateError } = await supabase
        .from('approvals')
        .update({ status })
        .eq('id', approvalId);
      if (updateError) {
        console.error(`Failed to update approval ${approvalId} status to ${status}:`, updateError);
        logSupabaseQueryError(updateError);
        throw new Error(getActionErrorMessage(updateError, 'Failed to update approval status.'));
      }
      console.log('[Approvals] Approval status updated', { approvalId, status });

      setApprovals((prev) =>
        sortApprovalsByRecency(
          prev.map((item) =>
            item.id === approvalId
              ? { ...item, status, updated_at: new Date().toISOString() }
              : item
          )
        )
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateLinkedTaskDecision = async (
    approval: ApprovalItem,
    decision: 'APPROVED' | 'REJECTED'
  ): Promise<void> => {
    if (!approval.task_id) {
      return;
    }
    let updatePayload: Record<string, unknown>;

    if (isTaskExtensionApproval(approval)) {
      const linkedTask = approval.linkedTask;
      const requestedDueDate = parseDateTimeMs(
        linkedTask?.requestedDueDate ?? (linkedTask as any)?.requested_due_date
      );

      updatePayload =
        decision === 'APPROVED'
          ? {
              extension_status: 'APPROVED',
              ...(requestedDueDate > 0 ? { deadline: requestedDueDate } : {}),
            }
          : {
              extension_status: 'REJECTED',
            };
    } else if (isHighPriorityTaskCompletionApproval(approval)) {
      const linkedTask = approval.linkedTask;
      updatePayload =
        decision === 'APPROVED'
          ? {
              status: 'completed',
              completedAt: Date.now(),
              ...(String(linkedTask?.extensionStatus || '').toUpperCase() === 'REQUESTED'
                ? { extension_status: 'REJECTED' }
                : {}),
            }
          : {
              status: 'in-progress',
              completedAt: null,
            };
    } else {
      return;
    }

    console.log('[Approvals] Updating linked task after approval decision', {
      approvalId: approval.id,
      taskId: approval.task_id,
      decision,
      updatePayload,
    });
    const { error: taskUpdateError } = await supabase
      .from('tasks')
      .update(updatePayload)
      .eq('id', approval.task_id);

    if (taskUpdateError) {
      console.error(
        `Failed to update linked task ${approval.task_id} after approval ${approval.id} was ${decision}:`,
        taskUpdateError
      );
      logSupabaseQueryError(taskUpdateError);
      throw new Error(getActionErrorMessage(taskUpdateError, 'Failed to update linked task.'));
    }
    console.log('[Approvals] Linked task updated after approval decision', {
      approvalId: approval.id,
      taskId: approval.task_id,
      decision,
    });

    if (decision === 'APPROVED' && isHighPriorityTaskCompletionApproval(approval)) {
      const { error: staleExtensionApprovalError } = await supabase
        .from('approvals')
        .update({ status: 'REJECTED' })
        .eq('task_id', approval.task_id)
        .ilike('title', `${EXTENSION_REQUEST_PREFIX}%`)
        .in('status', ['PENDING', 'NEEDS_REVIEW']);

      if (staleExtensionApprovalError) {
        console.warn('Failed to close stale extension approvals after task completion approval:', staleExtensionApprovalError);
        logSupabaseQueryError(staleExtensionApprovalError);
      }
    }
  };

  const getEmployeeDisplayName = useCallback((employeeId?: string | null): string => {
    const normalizedId = String(employeeId || '').trim();
    if (!normalizedId) {
      return 'Anyone / Unassigned';
    }
    const directoryMatch = companyEmployees.find((employee) => employee.id === normalizedId);
    if (directoryMatch?.name) {
      return directoryMatch.name;
    }
    return employeeNamesById[normalizedId] || getApproverName(normalizedId, approvers);
  }, [approvers, companyEmployees, employeeNamesById]);

  const getRequesterDisplayName = useCallback((approval: ApprovalItem): string => {
    const inlineRequesterName =
      String((approval as any).requester_name || (approval as any).requester || '').trim();
    if (inlineRequesterName) {
      return inlineRequesterName;
    }
    return getEmployeeDisplayName(approval.requester_id);
  }, [getEmployeeDisplayName]);

  const renderLinkedTaskInfoBox = useCallback((approval: ApprovalItem) => {
    const linkedTask = approval.linkedTask;
    if (!linkedTask) {
      return null;
    }

    return (
      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/90 p-3">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Linked Task</p>
        <div className="mt-2 space-y-2">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Task</p>
            <p className="text-sm font-semibold text-slate-900 break-words">{linkedTask.description || 'Untitled task'}</p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
              <User className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Assigned To</p>
                <p className="text-xs font-medium text-slate-700 break-words">{getEmployeeDisplayName(linkedTask.assignedTo)}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
              <User className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Assigned By</p>
                <p className="text-xs font-medium text-slate-700 break-words">{getEmployeeDisplayName(linkedTask.assignedBy)}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
              <Calendar className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Due Date</p>
                <p className="text-xs font-medium text-slate-700 break-words">{formatTaskDueDate(linkedTask.deadline)}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setViewingTaskApprovalId(approval.id)}
            className="inline-flex min-h-[38px] items-center gap-2 rounded-lg border border-[var(--accent)]/20 bg-white px-3 py-2 text-xs font-bold text-[var(--accent)] transition-colors hover:bg-[var(--accent-light)]"
          >
            <Eye className="h-3.5 w-3.5" />
            View Task
          </button>
        </div>
      </div>
    );
  }, [getEmployeeDisplayName]);

  const handleApprove = async (approvalId: string): Promise<void> => {
    await runApprovalAction(approvalId, 'approve', async () => {
      const approval = approvals.find((item) => item.id === approvalId) || selectedApproval;
      if (!approval) return;
      console.log('[Approvals] Starting approve flow', {
        approvalId,
        taskId: approval.task_id,
      });
      await updateStatus(approvalId, 'APPROVED');
      if (approval.task_id && (isHighPriorityTaskCompletionApproval(approval) || isTaskExtensionApproval(approval))) {
        console.log(approval);
        await updateLinkedTaskDecision(approval, 'APPROVED');
      }
      console.log('[Approvals] Approve flow completed', {
        approvalId,
        taskId: approval.task_id,
      });
      alert('Approval approved successfully.');
    });
  };

  const handleReject = async (approvalId: string): Promise<void> => {
    await runApprovalAction(approvalId, 'reject', async () => {
      const approval = approvals.find((item) => item.id === approvalId) || selectedApproval;
      if (!approval) return;
      await updateStatus(approvalId, 'REJECTED');
      if (approval.task_id && (isHighPriorityTaskCompletionApproval(approval) || isTaskExtensionApproval(approval))) {
        await updateLinkedTaskDecision(approval, 'REJECTED');
      }
      alert('Approval rejected successfully.');
    });
  };

  const handleDraftMessageChange = (value: string, caretIndex?: number) => {
    setDraftMessage(value);
    const mention = getMentionQuery(value, caretIndex ?? value.length);
    if (!mention) {
      setMentionMenuOpen(false);
      setMentionCandidates([]);
      setMentionStartIndex(null);
      return;
    }

    const query = mention.query.toLowerCase();
    const candidates = mentionableMembers
      .filter((member) => member.name.toLowerCase().includes(query))
      .slice(0, 8);
    setMentionStartIndex(mention.start);
    setMentionCandidates(candidates);
    setActiveMentionIndex(0);
    setMentionMenuOpen(candidates.length > 0);
  };

  const insertMention = (candidate: MentionCandidate) => {
    const textarea = approvalChatInputRef.current;
    const cursorPos = textarea?.selectionStart ?? draftMessage.length;
    const mentionStart = mentionStartIndex ?? Math.max(0, draftMessage.slice(0, cursorPos).lastIndexOf('@'));
    const before = draftMessage.slice(0, mentionStart);
    const after = draftMessage.slice(cursorPos);
    const mentionText = `@${candidate.name}`;
    const nextValue = `${before}${mentionText} ${after}`;
    const nextCaret = (before + mentionText + ' ').length;

    setDraftMessage(nextValue);
    setSelectedMentions((prev) => prev.some((entry) => entry.id === candidate.id) ? prev : [...prev, candidate]);
    setMentionMenuOpen(false);
    setMentionCandidates([]);
    setMentionStartIndex(null);
    setActiveMentionIndex(0);

    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const clearChatDraftState = () => {
    setMentionMenuOpen(false);
    setMentionCandidates([]);
    setMentionStartIndex(null);
    setActiveMentionIndex(0);
    setSelectedMentions([]);
    setReplyingToMessage(null);
    setEditingMessage(null);
    setShowAttachmentMenu(false);
    if (selectedChatMedia?.previewUrl) {
      URL.revokeObjectURL(selectedChatMedia.previewUrl);
    }
    setSelectedChatMedia(null);
  };

  const handleApprovalChatMediaSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_CHAT_MEDIA_BYTES) {
      setError('Attachment is too large. Maximum size is 50 MB.');
      return;
    }
    if (selectedChatMedia?.previewUrl) {
      URL.revokeObjectURL(selectedChatMedia.previewUrl);
    }
    const mediaType = getMediaTypeFromFile(file);
    setSelectedChatMedia({
      id: `approval-media-${Date.now()}`,
      file,
      mediaType,
      name: file.name || 'attachment',
      size: file.size,
      previewUrl: mediaType === 'image' || mediaType === 'video' || mediaType === 'audio'
        ? URL.createObjectURL(file)
        : null,
    });
    setShowAttachmentMenu(false);
  };

  const uploadApprovalChatMedia = async (
    approvalId: string,
    draft: PendingApprovalChatMedia
  ): Promise<Pick<ApprovalThread, 'media_url' | 'media_type' | 'media_name' | 'media_size'>> => {
    const storagePath = `${currentUser.company_id}/${approvalId}/${Date.now()}_${sanitizeFileName(draft.name)}`;
    const { error: uploadError } = await supabase.storage
      .from(CHAT_MEDIA_BUCKET)
      .upload(storagePath, draft.file, {
        contentType: draft.file.type || 'application/octet-stream',
        upsert: false,
      });
    if (uploadError) {
      throw new Error(uploadError.message || 'Failed to upload attachment.');
    }
    const { data } = supabase.storage.from(CHAT_MEDIA_BUCKET).getPublicUrl(storagePath);
    return {
      media_url: String(data?.publicUrl || '').trim(),
      media_type: draft.mediaType,
      media_name: draft.name,
      media_size: draft.size,
    };
  };

  const sendApprovalMentionNotifications = async (
    approval: ApprovalItem,
    messageText: string,
    mentionSnapshot: MentionCandidate[] = selectedMentions
  ) => {
    const normalizedText = messageText.toLowerCase();
    const mentionedIds = Array.from(
      new Set(
        mentionSnapshot
          .filter((candidate) => normalizedText.includes(`@${candidate.name.toLowerCase()}`))
          .map((candidate) => candidate.id)
      )
    ).filter((id) => id && id !== currentUser.id);

    if (!mentionedIds.length) return;

    const actorName = String(currentUser.name || currentUser.email?.split('@')[0] || 'A teammate').trim();
    const companyId = String(approval.company_id || currentUser.company_id || '').trim();
    await Promise.all(
      mentionedIds.map((employeeId) =>
        sendApprovalRequestNotification(
          employeeId,
          actorName,
          companyId,
          'You were mentioned in an approval chat',
          `${actorName} mentioned you in approval: ${approval.title || 'Approval'}`,
          approval.id
        )
      )
    );
  };

  const toggleApprovalReaction = async (message: ApprovalThreadView) => {
    const messageId = String(message.id || '').trim();
    if (!messageId || messageId.startsWith('temp-')) return;
    const currentUserId = currentUserAuthId || currentUser.id;
    const existing = (reactionsByMessageId[messageId] || []).find(
      (reaction) => reaction.user_id === currentUserId && reaction.emoji === '👍'
    );

    if (existing) {
      setReactionsByMessageId((prev) => ({
        ...prev,
        [messageId]: (prev[messageId] || []).filter((reaction) => reaction.id !== existing.id),
      }));
      const { error } = await supabase.from('message_reactions').delete().eq('id', existing.id);
      if (error) {
        console.warn('Failed to remove approval reaction:', error);
        logSupabaseQueryError(error);
        void loadApprovalThreadReactions(threads.map((thread) => thread.id));
      }
      return;
    }

    const optimisticReaction: MessageReaction = {
      id: `temp-reaction-${Date.now()}`,
      message_id: messageId,
      user_id: currentUserId,
      emoji: '👍',
    };
    setReactionsByMessageId((prev) => ({
      ...prev,
      [messageId]: [...(prev[messageId] || []), optimisticReaction],
    }));
    const { data, error } = await supabase
      .from('message_reactions')
      .insert({ message_id: messageId, user_id: currentUserId, emoji: '👍' })
      .select('id, message_id, user_id, emoji')
      .single();
    if (error) {
      console.warn('Failed to add approval reaction:', error);
      logSupabaseQueryError(error);
      void loadApprovalThreadReactions(threads.map((thread) => thread.id));
      return;
    }
    setReactionsByMessageId((prev) => ({
      ...prev,
      [messageId]: (prev[messageId] || []).map((reaction) =>
        reaction.id === optimisticReaction.id
          ? {
              id: String(data.id),
              message_id: String(data.message_id || messageId),
              user_id: String(data.user_id || currentUserId),
              emoji: String(data.emoji || '👍'),
            }
          : reaction
      ),
    }));
  };

  const renderApprovalChatMediaPreview = (message: ApprovalThreadView) => {
    const mediaUrl = String(message.media_url || '').trim();
    if (!mediaUrl) return null;
    if (message.media_type === 'image') {
      return <img src={mediaUrl} alt={message.media_name || 'Attachment'} className="mt-2 max-h-56 w-full rounded-xl object-cover" />;
    }
    if (message.media_type === 'video') {
      return <video src={mediaUrl} controls className="mt-2 max-h-56 w-full rounded-xl bg-black" />;
    }
    if (message.media_type === 'audio') {
      return <audio src={mediaUrl} controls className="mt-2 w-full" />;
    }
    return (
      <a href={mediaUrl} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-700">
        <FileText className="h-4 w-4" />
        <span className="min-w-0 flex-1 truncate">{message.media_name || 'Attachment'}</span>
        <Download className="h-4 w-4" />
      </a>
    );
  };

  const handleSendMessage = async (): Promise<void> => {
    if (!selectedApproval) return;
    const trimmed = draftMessage.trim();
    if (editingMessage) {
      if (!trimmed && !editingMessage.media_url) {
        setError('Message cannot be empty.');
        return;
      }
      const { error: editError } = await supabase
        .from('approval_threads')
        .update({
          message_text: trimmed,
          is_edited: true,
          edited_at: new Date().toISOString(),
        })
        .eq('id', editingMessage.id);
      if (editError) {
        setError(editError.message || 'Failed to edit message.');
        return;
      }
      setThreads((prev) =>
        prev.map((message) =>
          message.id === editingMessage.id
            ? { ...message, message_text: trimmed, is_edited: true, edited_at: new Date().toISOString() }
            : message
        )
      );
      setDraftMessage('');
      clearChatDraftState();
      return;
    }

    if (!trimmed && !selectedChatMedia) {
      setError('Message cannot be empty.');
      return;
    }
    const authenticatedUserId = await getAuthenticatedUserId();
    if (!authenticatedUserId) {
      setError('You must be signed in to send approval thread messages.');
      return;
    }

    const optimisticId = `temp-${Date.now()}`;
    const optimisticCreatedAt = new Date().toISOString();
    const mediaDraft = selectedChatMedia;
    const mentionSnapshot = selectedMentions;
    const optimisticMessage: ApprovalThreadView = {
      id: optimisticId,
      approval_id: selectedApproval.id,
      sender_id: authenticatedUserId,
      message_text: trimmed,
      created_at: optimisticCreatedAt,
      created_at_ms: parseDateTimeMs(optimisticCreatedAt) || Date.now(),
      sender_name: currentUser.name || 'You',
      reply_to_id: replyingToMessage?.id || null,
      reply_to: replyingToMessage
        ? {
            id: replyingToMessage.id,
            sender_name: replyingToMessage.sender_name,
            message_text: replyingToMessage.message_text,
            media_type: replyingToMessage.media_type || null,
            media_name: replyingToMessage.media_name || null,
          }
        : null,
      media_url: mediaDraft?.previewUrl || null,
      media_type: mediaDraft?.mediaType || null,
      media_name: mediaDraft?.name || null,
      media_size: mediaDraft?.size || null,
      is_edited: false,
      edited_at: null,
      optimistic: true,
      upload_progress: mediaDraft ? 8 : undefined,
    };

    setThreads((prev) => [...prev, optimisticMessage]);
    setDraftMessage('');
    clearChatDraftState();
    setError(null);
    let uploadProgressTimer: number | null = null;

    try {
      if (mediaDraft) {
        uploadProgressTimer = window.setInterval(() => {
          setThreads((prev) =>
            prev.map((message) =>
              message.id === optimisticId
                ? { ...message, upload_progress: Math.min(92, (message.upload_progress || 0) + 11) }
                : message
            )
          );
        }, 260);
      }

      const mediaPayload = mediaDraft ? await uploadApprovalChatMedia(selectedApproval.id, mediaDraft) : {};
      const richPayload = {
        approval_id: selectedApproval.id,
        sender_id: authenticatedUserId,
        message_text: trimmed,
        reply_to_id: replyingToMessage?.id || null,
        ...mediaPayload,
      };
      let { data, error: insertError } = await supabase
        .from('approval_threads')
        .insert(approvalThreadRichColumnsMissingRef.current ? {
          approval_id: selectedApproval.id,
          sender_id: authenticatedUserId,
          message_text: trimmed,
        } : richPayload)
        .select(approvalThreadRichColumnsMissingRef.current
          ? 'id, approval_id, sender_id, message_text, created_at'
          : 'id, approval_id, sender_id, message_text, created_at, reply_to_id, media_url, media_type, media_name, media_size, is_edited, edited_at')
        .single();

      if (insertError && isMissingThreadColumnError(insertError)) {
        approvalThreadRichColumnsMissingRef.current = true;
        const retry = await supabase
          .from('approval_threads')
          .insert({
            approval_id: selectedApproval.id,
            sender_id: authenticatedUserId,
            message_text: trimmed,
          })
          .select('id, approval_id, sender_id, message_text, created_at')
          .single();
        data = retry.data;
        insertError = retry.error;
      }

      if (insertError) {
        throw new Error(insertError.message || 'Failed to send message.');
      }

      setThreads((prev) =>
        prev.map((message) =>
          message.id === optimisticId
            ? {
                id: String(data.id),
                approval_id: String(data.approval_id || selectedApproval.id),
                sender_id: String(data.sender_id || authenticatedUserId),
                message_text: String(data.message_text || trimmed),
                created_at: String(data.created_at || new Date().toISOString()),
                created_at_ms: parseDateTimeMs(data.created_at) || Date.now(),
                sender_name: currentUser.name || 'You',
                reply_to_id: data.reply_to_id ? String(data.reply_to_id) : optimisticMessage.reply_to_id,
                reply_to: optimisticMessage.reply_to,
                media_url: data.media_url ? String(data.media_url) : mediaPayload.media_url || null,
                media_type: data.media_type || mediaPayload.media_type || null,
                media_name: data.media_name ? String(data.media_name) : mediaPayload.media_name || null,
                media_size: data.media_size === null || data.media_size === undefined
                  ? mediaPayload.media_size || null
                  : Number(data.media_size),
                is_edited: Boolean(data.is_edited),
                edited_at: data.edited_at ? String(data.edited_at) : null,
              }
            : message
        )
      );

      if (trimmed) {
        await sendApprovalMentionNotifications(selectedApproval, trimmed, mentionSnapshot);
      }
    } catch (sendError) {
      setThreads((prev) => prev.filter((message) => message.id !== optimisticId));
      setError(sendError instanceof Error ? sendError.message : 'Failed to send message.');
    } finally {
      if (uploadProgressTimer) window.clearInterval(uploadProgressTimer);
      if (mediaDraft?.previewUrl) URL.revokeObjectURL(mediaDraft.previewUrl);
    }
  };

  const handleAskForReview = async (): Promise<void> => {
    if (!selectedApproval) return;
    const trimmed = draftMessage.trim();
    if (!trimmed) {
      setError('Please type a review note before marking NEEDS_REVIEW.');
      return;
    }
    const authenticatedUserId = await getAuthenticatedUserId();
    if (!authenticatedUserId) {
      setError('You must be signed in to send approval thread messages.');
      return;
    }

    await runApprovalAction(selectedApproval.id, 'review', async () => {
      setUpdatingStatus(true);
      setError(null);
      try {
        const { error: messageError } = await supabase
          .from('approval_threads')
          .insert({
            approval_id: selectedApproval.id,
            sender_id: authenticatedUserId,
            message_text: trimmed,
          });
        if (messageError) throw messageError;

        const { error: statusError } = await supabase
          .from('approvals')
          .update({ status: 'NEEDS_REVIEW' })
          .eq('id', selectedApproval.id);
        if (statusError) throw statusError;

        setApprovals((prev) =>
          sortApprovalsByRecency(
            prev.map((item) =>
              item.id === selectedApproval.id
                ? { ...item, status: 'NEEDS_REVIEW', updated_at: new Date().toISOString() }
                : item
            )
          )
        );
        setDraftMessage('');
        await loadThreads(selectedApproval.id);
      } catch (reviewErr: any) {
        setError(reviewErr?.message || 'Failed to request review.');
      } finally {
        setUpdatingStatus(false);
      }
    });
  };

  const handleEscalateToAdmin = async (approvalId: string) => {
    const selectedEscalationAdmin = selectedEscalationAdminByApproval[approvalId] || '';
    if (!selectedEscalationAdmin) {
      alert('Please select a Super Admin to escalate to.');
      return;
    }

    await runApprovalAction(approvalId, 'escalate', async () => {
      try {
        const { error: updateError } = await supabase
          .from('approvals')
          .update({
            isEscalated: true,
            adminEscalationStatus: 'PENDING',
            escalated_to: selectedEscalationAdmin,
          })
          .eq('id', approvalId);

        if (updateError) throw updateError;

        setApprovals((prev) =>
          sortApprovalsByRecency(
            prev.map((app) =>
              app.id === approvalId
                ? {
                    ...app,
                    isEscalated: true,
                    adminEscalationStatus: 'PENDING',
                    escalated_to: selectedEscalationAdmin,
                    updated_at: new Date().toISOString(),
                  }
                : app
            )
          )
        );

        alert('Successfully escalated to Super Admin!');
        setSelectedEscalationAdminByApproval((prev) => {
          const next = { ...prev };
          delete next[approvalId];
          return next;
        });
      } catch (error: any) {
        console.error('Escalation error:', error);
        logSupabaseQueryError(error);
        alert('Failed to escalate: ' + error.message);
      }
    });
  };

  const handleAdminEscalationDecision = async (decision: 'APPROVED' | 'REJECTED'): Promise<void> => {
    if (!selectedApproval) return;

    await runApprovalAction(
      selectedApproval.id,
      decision === 'APPROVED' ? 'admin_approve' : 'admin_reject',
      async () => {
        setUpdatingStatus(true);
        setError(null);
        try {
          const { error: decisionError } = await supabase
            .from('approvals')
            .update({ adminEscalationStatus: decision })
            .eq('id', selectedApproval.id);
          if (decisionError) throw decisionError;

          setApprovals((prev) =>
            sortApprovalsByRecency(
              prev.map((item) =>
                item.id === selectedApproval.id
                  ? { ...item, adminEscalationStatus: decision }
                  : item
              )
            )
          );
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to update escalation decision.');
        } finally {
          setUpdatingStatus(false);
        }
      }
    );
  };

  const handleFinalApproval = async (): Promise<void> => {
    if (!selectedApproval) return;

    await runApprovalAction(selectedApproval.id, 'final_approve', async () => {
      setUpdatingStatus(true);
      setError(null);
      try {
        const { error: approveError } = await supabase
          .from('approvals')
          .update({ status: 'APPROVED' })
          .eq('id', selectedApproval.id);
        if (approveError) throw approveError;

        setApprovals((prev) =>
          sortApprovalsByRecency(
            prev.map((item) =>
              item.id === selectedApproval.id
                ? { ...item, status: 'APPROVED' }
                : item
            )
          )
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to approve request.');
      } finally {
        setUpdatingStatus(false);
      }
    });
  };

  const renderUnreadIndicator = (count: number) => (
    <div className="relative h-7 w-7 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 shrink-0">
      <MessageSquare className="w-4 h-4" />
      {count > 0 && (
        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </div>
  );

  const renderApprovalChat = (approval: ApprovalItem) => {
    const canSend = Boolean(draftMessage.trim() || selectedChatMedia);
    return (
      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-[#f7f4ee]">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white/80 px-3 py-2">
          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Messages</p>
            <p className="text-xs text-slate-500">Approval discussion</p>
          </div>
          {loadingThreads && <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Syncing...</span>}
        </div>

        <div className="max-h-72 space-y-3 overflow-y-auto px-3 py-3">
          {threads.length === 0 ? (
            <p className="rounded-xl bg-white/70 px-3 py-2 text-center text-xs text-slate-500">No messages yet.</p>
          ) : (
            threads.map((thread, index) => {
              const mine = isCurrentUserSenderId(thread.sender_id);
              const showDate =
                index === 0 ||
                getChatDateLabel(thread.created_at_ms) !== getChatDateLabel(threads[index - 1].created_at_ms);
              const reactions = reactionsByMessageId[thread.id] || [];
              const thumbsUpCount = reactions.filter((reaction) => reaction.emoji === '👍').length;
              const reactedByMe = reactions.some(
                (reaction) => reaction.user_id === (currentUserAuthId || currentUser.id) && reaction.emoji === '👍'
              );

              return (
                <div key={thread.id}>
                  {showDate && (
                    <div className="mb-2 flex justify-center">
                      <span className="rounded-full bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500 shadow-sm">
                        {getChatDateLabel(thread.created_at_ms)}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[86%]">
                      <div
                        className={`rounded-2xl px-3 py-2 shadow-sm ${
                          mine
                            ? 'rounded-br-md bg-[var(--accent)] text-white'
                            : 'rounded-bl-md bg-white text-slate-800'
                        }`}
                        onTouchStart={(event) => {
                          const touch = event.touches[0];
                          approvalChatTouchGestureRef.current = {
                            message: thread,
                            startX: touch.clientX,
                            startY: touch.clientY,
                            replyTriggered: false,
                          };
                        }}
                        onTouchMove={(event) => {
                          const gesture = approvalChatTouchGestureRef.current;
                          const touch = event.touches[0];
                          if (!gesture || !touch || gesture.replyTriggered) return;
                          const dx = touch.clientX - gesture.startX;
                          const dy = touch.clientY - gesture.startY;
                          if (Math.abs(dx) > 54 && Math.abs(dx) > Math.abs(dy) * 1.4) {
                            gesture.replyTriggered = true;
                            setReplyingToMessage(gesture.message);
                          }
                        }}
                        onTouchEnd={() => {
                          approvalChatTouchGestureRef.current = null;
                        }}
                      >
                        {thread.reply_to && (
                          <button
                            type="button"
                            className={`mb-2 block w-full rounded-xl border-l-4 px-2 py-1 text-left ${
                              mine ? 'border-white/60 bg-white/15 text-white' : 'border-[var(--accent)] bg-slate-50 text-slate-700'
                            }`}
                          >
                            <p className="truncate text-[11px] font-bold">{thread.reply_to.sender_name}</p>
                            <p className="line-clamp-1 text-[11px] opacity-80">{getQuotedMessageText(thread.reply_to)}</p>
                          </button>
                        )}
                        {renderApprovalChatMediaPreview(thread)}
                        {thread.optimistic && typeof thread.upload_progress === 'number' && (
                          <div className="mt-2 h-1.5 rounded-full bg-white/30">
                            <div className="h-full rounded-full bg-white" style={{ width: `${thread.upload_progress}%` }} />
                          </div>
                        )}
                        {thread.message_text && (
                          <p className={`break-words text-sm leading-relaxed ${thread.media_url ? 'mt-2' : ''}`}>{thread.message_text}</p>
                        )}
                        <div className={`mt-1 flex items-center gap-1 text-[10px] ${mine ? 'justify-end text-white/70' : 'text-slate-400'}`}>
                          <span>{formatDateTime(thread.created_at)}</span>
                          {thread.is_edited && <span>(edited)</span>}
                        </div>
                      </div>
                      <div className={`mt-1 flex items-center gap-1 ${mine ? 'justify-end' : 'justify-start'}`}>
                        <button
                          type="button"
                          onClick={() => setReplyingToMessage(thread)}
                          className="min-h-[32px] rounded-full px-2 text-slate-500 hover:bg-white"
                          title="Reply"
                        >
                          <Reply className="h-3.5 w-3.5" />
                        </button>
                        {mine && isMessageWithinEditWindow(thread.created_at) && !thread.optimistic && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMessage(thread);
                              setReplyingToMessage(null);
                              setDraftMessage(thread.message_text || '');
                              requestAnimationFrame(() => approvalChatInputRef.current?.focus());
                            }}
                            className="min-h-[32px] rounded-full px-2 text-slate-500 hover:bg-white"
                            title="Edit"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {thumbsUpCount > 0 ? (
                          <MessageReactionChip
                            messageId={thread.id}
                            count={thumbsUpCount}
                            reactedByMe={reactedByMe}
                            names={getApprovalReactionDisplayNames(thread.id)}
                            onToggle={() => void toggleApprovalReaction(thread)}
                            onRequestNames={fetchApprovalReactionNames}
                            className={`min-h-[32px] rounded-full border px-2 text-xs ${
                              reactedByMe ? 'border-[var(--accent)] bg-white text-[var(--accent)]' : 'border-transparent text-slate-500 hover:bg-white'
                            }`}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => void toggleApprovalReaction(thread)}
                            className="min-h-[32px] rounded-full border border-transparent px-2 text-xs text-slate-500 hover:bg-white"
                            title="React with thumbs up"
                          >
                            👍
                          </button>
                        )}
                      </div>
                      <p className={`mt-0.5 text-[10px] font-bold text-slate-500 ${mine ? 'text-right' : 'text-left'}`}>{thread.sender_name}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="relative border-t border-slate-200 bg-white p-2">
          <input ref={approvalChatPhotoInputRef} type="file" accept="image/*,video/*,audio/*" className="hidden" onChange={handleApprovalChatMediaSelection} />
          <input ref={approvalChatDocumentInputRef} type="file" className="hidden" onChange={handleApprovalChatMediaSelection} />

          {mentionMenuOpen && (
            <div className="absolute bottom-full left-3 z-20 mb-2 max-h-48 w-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-xl">
              {mentionCandidates.map((candidate, index) => (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => insertMention(candidate)}
                  className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold ${
                    index === activeMentionIndex ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[10px] font-black">
                    {candidate.name.slice(0, 2).toUpperCase()}
                  </span>
                  {candidate.name}
                </button>
              ))}
            </div>
          )}

          {(replyingToMessage || editingMessage || selectedChatMedia) && (
            <div className="mb-2 rounded-2xl border border-slate-200 bg-slate-50 p-2">
              {editingMessage ? (
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Editing message</p>
              ) : replyingToMessage ? (
                <>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Replying to {replyingToMessage.sender_name}</p>
                  <p className="line-clamp-1 text-sm text-slate-700">{getQuotedMessageText(replyingToMessage)}</p>
                </>
              ) : null}
              {selectedChatMedia && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-white">
                    {selectedChatMedia.mediaType === 'image' && selectedChatMedia.previewUrl ? (
                      <img src={selectedChatMedia.previewUrl} alt={selectedChatMedia.name} className="h-full w-full object-cover" />
                    ) : selectedChatMedia.mediaType === 'video' ? (
                      <Video className="h-5 w-5 text-slate-500" />
                    ) : selectedChatMedia.mediaType === 'audio' ? (
                      <Music4 className="h-5 w-5 text-slate-500" />
                    ) : (
                      <FileText className="h-5 w-5 text-slate-500" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">{selectedChatMedia.name}</p>
                    <p className="text-xs text-slate-500">{formatChatFileSize(selectedChatMedia.size)}</p>
                  </div>
                </div>
              )}
              <button type="button" onClick={clearChatDraftState} className="mt-2 text-xs font-bold text-slate-500">Cancel</button>
            </div>
          )}

          <div className="flex items-end gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAttachmentMenu((prev) => !prev)}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                title="Attach"
              >
                <Paperclip className="h-5 w-5" />
              </button>
              {showAttachmentMenu && (
                <div className="absolute bottom-full left-0 mb-2 w-44 rounded-2xl border border-slate-200 bg-white p-1 shadow-xl">
                  <button type="button" onClick={() => approvalChatPhotoInputRef.current?.click()} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    <ImageIcon className="h-4 w-4" /> Photo / media
                  </button>
                  <button type="button" onClick={() => approvalChatDocumentInputRef.current?.click()} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    <FileText className="h-4 w-4" /> Document
                  </button>
                </div>
              )}
            </div>
            <textarea
              ref={approvalChatInputRef}
              value={draftMessage}
              onChange={(e) => handleDraftMessageChange(e.target.value, e.target.selectionStart)}
              placeholder={editingMessage ? 'Edit your message...' : 'Type a message... Use @ to mention'}
              className="min-h-[44px] max-h-28 flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              rows={1}
              onKeyDown={(e) => {
                if (mentionMenuOpen && mentionCandidates.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActiveMentionIndex((prev) => (prev + 1) % mentionCandidates.length);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActiveMentionIndex((prev) => (prev - 1 + mentionCandidates.length) % mentionCandidates.length);
                    return;
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    insertMention(mentionCandidates[activeMentionIndex]);
                    return;
                  }
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSendMessage();
                }
              }}
            />
            <button
              type="button"
              onClick={() => void handleSendMessage()}
              disabled={updatingStatus || !canSend}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-[var(--accent)] text-white transition-all hover:bg-[#4338CA] disabled:opacity-40"
              title="Send message"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
          {approval.requester_id === currentUser.id && approval.status === 'PENDING' && (
            <LoadingButton
              type="button"
              onClick={() => void handleAskForReview()}
              isLoading={processingApprovalId === approval.id && processingApprovalAction === 'review'}
              loadingText="Requesting..."
              variant="secondary"
              disabled={updatingStatus || !draftMessage.trim()}
              className="mt-2 min-h-[44px] px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold transition-all disabled:opacity-50"
            >
              Request Review
            </LoadingButton>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="w-full bg-white border border-[var(--border)] rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="section-kicker">Approvals</p>
          <h2 className="text-lg font-black text-[var(--ink)] mt-1">Requests & Decisions</h2>
        </div>
        {currentUser.role !== 'owner' && currentUser.role !== 'super_admin' && (
          <button
            onClick={() => setIsApprovalModalOpen(true)}
            className="w-full sm:w-auto min-h-[44px] bg-[var(--accent)] hover:bg-[#4338CA] text-white rounded-2xl px-4 py-3 sm:py-2.5 shadow-[0_4px_14px_rgba(79,70,229,0.15)] flex items-center justify-center gap-2 transition-all duration-200 active:scale-95"
          >
            <Plus className="w-5 h-5" />
            <span className="font-semibold">Approval</span>
          </button>
        )}
      </div>

      <div className={`mt-4 flex flex-col sm:flex-row gap-2 ${currentUser.role === 'owner' || currentUser.role === 'super_admin' ? '' : ''}`}>
        {currentUser.role !== 'owner' && currentUser.role !== 'super_admin' && (
          <button
            onClick={() => setView('my_requests')}
            className={`w-full sm:w-auto min-h-[44px] px-3 py-2 rounded-xl text-sm font-bold border ${
              view === 'my_requests'
                ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                : 'bg-white text-slate-700 border-slate-200'
            }`}
          >
            My Requests
          </button>
        )}
        {!isStaffOnly && (
          <button
            onClick={() => setView('needs_my_approval')}
            className={`w-full sm:w-auto min-h-[44px] px-3 py-2 rounded-xl text-sm font-bold border ${
              view === 'needs_my_approval'
                ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                : 'bg-white text-slate-700 border-slate-200'
            }`}
          >
            Needs My Approval
          </button>
        )}
      </div>

      {canUsePendingApprovedTabs && (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 pill-shell p-1.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleOpenPendingTab}
              className={`flex-1 sm:flex-none min-h-[40px] rounded-xl px-4 py-1.5 text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                approvalTab === 'pending'
                  ? 'pill-active text-[var(--orange)]'
                  : 'text-[var(--ink-3)] hover:bg-white/60'
              }`}
            >
              <span>Pending</span>
              <span className="font-ui-mono inline-flex min-w-[22px] h-[22px] px-1.5 items-center justify-center rounded-full bg-[var(--orange)] text-white text-[11px] font-medium leading-none">
                {pendingApprovalCount}
              </span>
            </button>
            <button
              type="button"
              onClick={handleOpenApprovedTab}
              className={`flex-1 sm:flex-none min-h-[40px] rounded-xl px-4 py-1.5 text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                approvalTab === 'approved'
                  ? 'pill-active text-[var(--green)]'
                  : 'text-[var(--ink-3)] hover:bg-white/60'
              }`}
            >
              <span>Approval Status</span>
              <span className="font-ui-mono inline-flex min-w-[22px] h-[22px] px-1.5 items-center justify-center rounded-full bg-[var(--green)] text-white text-[11px] font-medium leading-none">
                {approvedTodayCount}
              </span>
            </button>
          </div>

          {approvalTab === 'approved' && (
            <div className="relative self-end sm:self-auto" ref={approvedDateFilterRef}>
              <button
                type="button"
                onClick={() => setShowApprovedDateFilterMenu((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-xl border border-[var(--border)] bg-white px-3 py-2 text-xs font-semibold text-[var(--ink-2)] hover:bg-slate-50 transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
                title="Filter approved decisions by date"
              >
                <Filter className="w-3.5 h-3.5" />
                <span>{approvedFilterLabel}</span>
              </button>

              {showApprovedDateFilterMenu && (
                <div className="absolute right-0 mt-2 w-60 rounded-xl border border-slate-200 bg-white p-2 shadow-lg z-20 space-y-1">
                  <button
                    type="button"
                    onClick={() => {
                      setApprovedDateFilter('today');
                      setShowApprovedDateFilterMenu(false);
                    }}
                    className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      approvedDateFilter === 'today' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setApprovedDateFilter('yesterday');
                      setShowApprovedDateFilterMenu(false);
                    }}
                    className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      approvedDateFilter === 'yesterday' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Yesterday
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setApprovedDateFilter('last7');
                      setShowApprovedDateFilterMenu(false);
                    }}
                    className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      approvedDateFilter === 'last7' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Last 7 days
                  </button>
                  <button
                    type="button"
                    onClick={() => setApprovedDateFilter('custom')}
                    className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      approvedDateFilter === 'custom' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Custom range
                  </button>

                  {approvedDateFilter === 'custom' && (
                    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-2">
                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">From</label>
	                        <div className="flex items-center gap-2">
	                          <input
	                            type="date"
	                            value={approvedFromDate}
	                            onChange={(event) => setApprovedFromDate(event.target.value)}
	                            onInput={(event) => setApprovedFromDate(event.currentTarget.value)}
	                            onBlur={(event) => setApprovedFromDate(event.currentTarget.value)}
	                            onFocus={(event) => syncInputOnPickerClose(event.currentTarget, setApprovedFromDate)}
	                            className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-900"
	                          />
	                          {approvedFromDate && (
	                            <button
	                              type="button"
	                              onClick={() => setApprovedFromDate('')}
	                              className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
	                              aria-label="Clear date"
	                            >
	                              X Clear
	                            </button>
	                          )}
	                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</label>
	                        <div className="flex items-center gap-2">
	                          <input
	                            type="date"
	                            value={approvedToDate}
	                            onChange={(event) => setApprovedToDate(event.target.value)}
	                            onInput={(event) => setApprovedToDate(event.currentTarget.value)}
	                            onBlur={(event) => setApprovedToDate(event.currentTarget.value)}
	                            onFocus={(event) => syncInputOnPickerClose(event.currentTarget, setApprovedToDate)}
	                            className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-900"
	                          />
	                          {approvedToDate && (
	                            <button
	                              type="button"
	                              onClick={() => setApprovedToDate('')}
	                              className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
	                              aria-label="Clear date"
	                            >
	                              X Clear
	                            </button>
	                          )}
	                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Status Filter Pills - Only show for My Requests */}
      {view === 'my_requests' && !canUsePendingApprovedTabs && (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all' as const, label: 'All' },
              { key: 'pending' as const, label: 'Pending' },
              { key: 'completed' as const, label: 'Completed' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`min-h-[36px] px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === key
                    ? 'bg-[var(--accent)] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          
          {/* Month Filter Icon */}
          <div className="relative">
            <button
              onClick={() => setShowMonthDropdown(!showMonthDropdown)}
              className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 transition-colors"
              title="Filter by month or date range"
            >
              <Filter className="w-4 h-4" />
            </button>
            
            {/* Month Dropdown */}
            {showMonthDropdown && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                <div className="max-h-60 overflow-y-auto">
                  <button
                    onClick={() => {
                      setMonthFilter('all');
                      setDateRangeFilter('all');
                      setStartDate('');
                      setEndDate('');
                      setShowMonthDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors ${
                      monthFilter === 'all' && dateRangeFilter === 'all' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-700'
                    }`}
                  >
                    All Time
                  </button>
                  {getAvailableMonths(approvals).map(month => (
                    <button
                      key={month}
                      onClick={() => {
                        setMonthFilter(month);
                        setDateRangeFilter('all');
                        setStartDate('');
                        setEndDate('');
                        setShowMonthDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors ${
                        monthFilter === month && dateRangeFilter === 'all' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-700'
                      }`}
                    >
                      {month}
                    </button>
                  ))}
                  
                  {/* Divider */}
                  <div className="border-t border-slate-200 my-1"></div>
                  
                  {/* Calendar Option */}
                  <button
                    onClick={() => {
                      setShowDatePicker(true);
                      setShowMonthDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors flex items-center gap-2 ${
                      dateRangeFilter === 'custom' ? 'bg-[var(--accent-light)] text-[var(--accent)]' : 'text-slate-700'
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    Custom Date Range
                  </button>
                </div>
              </div>
            )}
            
            {/* Date Range Picker Modal */}
            {showDatePicker && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Select Date Range</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
	                      <div className="flex items-center gap-2">
	                        <input
	                          type="date"
	                          value={startDate}
	                          onChange={(e) => setStartDate(e.target.value)}
	                          onInput={(e) => setStartDate(e.currentTarget.value)}
	                          onBlur={(e) => setStartDate(e.currentTarget.value)}
	                          onFocus={(e) => syncInputOnPickerClose(e.currentTarget, setStartDate)}
	                          className="flex-1 min-h-[48px] px-3 py-2 border border-slate-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
	                        />
	                        {startDate && (
	                          <button
	                            type="button"
	                            onClick={() => setStartDate('')}
	                            className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
	                            aria-label="Clear date"
	                          >
	                            X Clear
	                          </button>
	                        )}
	                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
	                      <div className="flex items-center gap-2">
	                        <input
	                          type="date"
	                          value={endDate}
	                          onChange={(e) => setEndDate(e.target.value)}
	                          onInput={(e) => setEndDate(e.currentTarget.value)}
	                          onBlur={(e) => setEndDate(e.currentTarget.value)}
	                          onFocus={(e) => syncInputOnPickerClose(e.currentTarget, setEndDate)}
	                          className="flex-1 min-h-[48px] px-3 py-2 border border-slate-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
	                        />
	                        {endDate && (
	                          <button
	                            type="button"
	                            onClick={() => setEndDate('')}
	                            className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
	                            aria-label="Clear date"
	                          >
	                            X Clear
	                          </button>
	                        )}
	                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-6">
                      <button
                        onClick={() => {
                          setShowDatePicker(false);
                          setStartDate('');
                          setEndDate('');
                          setDateRangeFilter('all');
                          setMonthFilter(getCurrentMonth());
                        }}
                        className="flex-1 min-h-[44px] px-4 py-2 border border-slate-200 rounded-lg text-base text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    <button
                      onClick={() => {
                        if (startDate || endDate) {
                          setDateRangeFilter('custom');
                          setMonthFilter('all');
                          setShowDatePicker(false);
                        }
                      }}
                      disabled={!startDate && !endDate}
                      className="flex-1 min-h-[44px] px-4 py-2 bg-[var(--accent)] text-white rounded-lg text-base hover:bg-[#4338CA] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approval Creation Modal */}
      {isApprovalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={() => !creatingApproval && setIsApprovalModalOpen(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white w-full max-w-lg sm:max-w-xl h-[90vh] sm:h-auto rounded-t-3xl sm:rounded-2xl shadow-xl overflow-y-auto animate-in fade-in zoom-in-95">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 sm:p-5 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[var(--accent)]" />
                Create Approval Request
              </h2>
              {!creatingApproval && (
                <button
                  onClick={() => setIsApprovalModalOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Form */}
            <div className="p-4 sm:p-6 space-y-3">
              <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">Create Request</p>
              <div className="grid grid-cols-1 gap-2">
                <input
                  value={requestTitle}
                  onChange={(event) => setRequestTitle(event.target.value)}
                  placeholder="Request title"
                  className="min-h-[48px] px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
                  autoFocus
                />
                <div className="relative">
                  <textarea
                    value={requestDescription}
                    onChange={(event) => setRequestDescription(event.target.value)}
                    placeholder="Description"
                    className="min-h-[90px] w-full px-3 py-2 pb-10 rounded-xl border border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-900/20 resize-none"
                  />
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      attachmentsInputRef.current?.click();
                    }}
                    className="absolute bottom-2 right-2 p-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
                    title="Attach files"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    ref={attachmentsInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.xls,.xlsx,.csv,image/*"
                    className="hidden"
                    onChange={(event) => {
                      try {
                        const files = Array.from(event.target.files || []);
                        if (!files.length) return;

                        setRequestAttachments((prev) => {
                          const existing = new Set(prev.map((item) => `${item.name}_${item.size}`));
                          const next = [...prev];

                          for (const file of files) {
                            const key = `${file.name}_${file.size}`;
                            if (existing.has(key)) continue;

                            const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
                            attachmentFilesRef.current[id] = file;
                            next.push({
                              id,
                              name: file.name,
                              contentType: file.type || '',
                              size: file.size || 0,
                            });
                            existing.add(key);
                          }

                          return next;
                        });
                      } catch (attachError: any) {
                        setError(attachError?.message || 'Failed to attach selected files.');
                      } finally {
                        event.currentTarget.value = '';
                      }
                    }}
                  />
                </div>
                {requestAttachments.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {requestAttachments.map((file, index) => (
                      <div key={`${file.name}_${file.size}_${index}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
                        <p className="text-xs text-slate-700 truncate">
                          {file.name}
                          {file.size ? ` (${formatAttachmentSize(file.size)})` : ''}
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setRequestAttachments((prev) => {
                              const toRemove = prev[index];
                              if (toRemove?.id) {
                                delete attachmentFilesRef.current[toRemove.id];
                              }
                              return prev.filter((_, i) => i !== index);
                            })
                          }
                          className="p-1 rounded-md text-slate-500 hover:bg-slate-200"
                          title="Remove attachment"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    value={requestAmount}
                    onChange={(event) => setRequestAmount(event.target.value)}
                    placeholder="Amount (INR)"
                    type="number"
                    min="0"
                    className="min-h-[48px] px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
                  />
                  <select
                    value={requestApproverId}
                    onChange={(event) => setRequestApproverId(event.target.value)}
                    className="min-h-[48px] px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-900/20"
                  >
                    {loadingApprovers ? (
                      <option value="">Loading approvers...</option>
                    ) : approvers.length === 0 ? (
                      <option value="">No approver available</option>
                    ) : (
                      approvers.map((approver) => (
                        <option key={approver.id} value={approver.id}>
                          {approver.name} ({approver.role})
                        </option>
                      ))
                    )}
                  </select>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsApprovalModalOpen(false)}
                    disabled={creatingApproval}
                    className="flex-1 min-h-[48px] rounded-xl bg-slate-100 text-slate-700 text-base font-bold hover:bg-slate-200 transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <LoadingButton
                    type="button"
                    onClick={() => void handleCreateRequest()}
                    isLoading={creatingApproval}
                    loadingText="Creating..."
                    variant="primary"
                    className="flex-1 min-h-[48px] rounded-xl bg-[var(--accent)] hover:bg-[#4338CA] text-white text-base font-bold disabled:opacity-60"
                  >
                    Create & Tag Approver
                  </LoadingButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 text-xs text-rose-700">
          {error}
        </div>
      )}

      <div className="mt-4 space-y-4">
        {loadingApprovals ? (
          <div className="text-sm text-slate-500">Loading approvals...</div>
        ) : (
          (() => {
            // Filter approvals based on active view
            let filteredApprovals = view === 'my_requests'
              ? filterApprovalsByStatus(approvals, statusFilter)
              : filterNeedsMyApprovalQueue(approvals);

            // Apply existing month/date filters for staff-only My Requests view
            if (view === 'my_requests' && !canUsePendingApprovedTabs) {
              if (dateRangeFilter === 'custom') {
                filteredApprovals = filterApprovalsByDateRange(filteredApprovals, startDate, endDate);
              } else {
                filteredApprovals = filterApprovalsByMonth(filteredApprovals, monthFilter);
              }
            }

            if (canUsePendingApprovedTabs) {
              if (approvalTab === 'pending') {
                filteredApprovals = filteredApprovals
                  .filter((approval) => isPendingApprovalStatus(approval.status))
                  .sort((a, b) => {
                    const aMs = parseDateTimeMs(a.created_at) || approvalActivityMs(a);
                    const bMs = parseDateTimeMs(b.created_at) || approvalActivityMs(b);
                    return bMs - aMs;
                  });
              } else {
                const approvedItems = filteredApprovals
                  .filter((approval) => isApprovedApprovalStatus(approval.status))
                  .sort((a, b) => {
                    const aMs = parseDateTimeMs(getApprovalUpdatedAtValue(a)) || approvalActivityMs(a);
                    const bMs = parseDateTimeMs(getApprovalUpdatedAtValue(b)) || approvalActivityMs(b);
                    return bMs - aMs;
                  });
                filteredApprovals = approvedItems.filter(isDecisionWithinApprovedFilter);
              }
            }

            if (filteredApprovals.length === 0) {
              return (
                <div className="text-sm text-slate-500">
                  {canUsePendingApprovedTabs
                    ? approvalTab === 'pending'
                      ? 'No pending approvals found.'
                      : approvedDateFilter === 'today'
                      ? 'No approved/rejected items found for today.'
                      : approvedDateFilter === 'yesterday'
                      ? 'No approved/rejected items found for yesterday.'
                      : approvedDateFilter === 'last7'
                      ? 'No approved/rejected items found in last 7 days.'
                      : 'No approved/rejected items found in selected date range.'
                    : view === 'my_requests'
                    ? statusFilter === 'pending'
                      ? 'No pending requests found.'
                      : statusFilter === 'completed'
                      ? 'No completed requests found.'
                      : dateRangeFilter === 'custom'
                      ? `No requests found in selected date range.`
                      : monthFilter !== 'all'
                      ? `No requests found for ${monthFilter}.`
                      : 'No requests found.'
                    : 'No approvals found for this view.'}
                </div>
              );
            }

            // Group approvals by month (only for My Requests)
            if (view === 'my_requests' && !canUsePendingApprovedTabs) {
              const groupedApprovals = groupApprovalsByMonth(filteredApprovals);
              const sortedMonths = Object.keys(groupedApprovals).sort((a, b) => {
                // Sort months chronologically (most recent first)
                return new Date(b).getTime() - new Date(a).getTime();
              });

              return sortedMonths.map((monthYear) => (
                <div key={monthYear} className="space-y-3">
                  {/* Month Header */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <p className="text-xs font-bold text-slate-700">{monthYear}</p>
                  </div>
                  
                  {/* Approvals for this month */}
                  <div className="space-y-3">
                    {groupedApprovals[monthYear].map((approval) => {
                      const isSelected = selectedApprovalId === approval.id;
	                      const unreadCount = approvalUnreadCountById[approval.id] || 0;
		                      const parsedApproval = extractApprovalAttachments(approval.description);
		                      const directApprovalForCurrentUser = currentApproverIdSet.has(String(approval.approver_id || '').trim());
		                      const escalatedApprovalForCurrentUser =
		                        isAdminApprover &&
		                        approval.isEscalated &&
		                        currentApproverIdSet.has(String(approval.escalated_to || '').trim());
		                      const canFinalizeAdminRejectedEscalation =
		                        directApprovalForCurrentUser &&
		                        approval.adminEscalationStatus === 'REJECTED';
		                      const canTakeActionOnApproval =
		                        isSelected &&
		                        !LOCKED_STATUSES.includes(approval.status) &&
		                        (
		                          directApprovalForCurrentUser ||
		                          (escalatedApprovalForCurrentUser && approval.adminEscalationStatus === 'PENDING') ||
		                          canFinalizeAdminRejectedEscalation
		                        );
                      const isProcessing = processingApprovalId === approval.id;
                      const isApproving = isProcessing && processingApprovalAction === 'approve';
                      const isRejecting = isProcessing && processingApprovalAction === 'reject';
                      const isEscalating = isProcessing && processingApprovalAction === 'escalate';
                      const isAdminApproving = isProcessing && processingApprovalAction === 'admin_approve';
                      const isAdminRejecting = isProcessing && processingApprovalAction === 'admin_reject';
                      const isFinalApproving = isProcessing && processingApprovalAction === 'final_approve';
                      const isReviewing = isProcessing && processingApprovalAction === 'review';

                      return (
                        <div
                          key={approval.id}
                          className={`w-full rounded-2xl border p-4 transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)] ${
                            canUsePendingApprovedTabs && approvalTab === 'approved' && isApprovedApprovalStatus(approval.status)
                              ? 'border-emerald-200 bg-[var(--green-light)]/55 border-l-[3px] border-l-[var(--green)]'
                              : isSelected
                              ? 'border-amber-300 bg-[var(--orange-light)]/70 border-l-[3px] border-l-[var(--orange)]'
                              : 'border-[var(--border)] bg-white border-l-[3px] border-l-[var(--orange)]'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleApprovalSelection(approval.id)}
                            className="w-full text-left"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-bold text-[var(--ink)] truncate">{approval.title || 'Untitled'}</p>
                              <div className="flex items-center gap-2">
                                {renderUnreadIndicator(unreadCount)}
                                  <span className={`font-ui-mono text-[10px] font-medium uppercase px-2 py-1 rounded-full ${getStatusClasses(approval.status)}`}>
                                    {approval.status}
                                  </span>
                                </div>
                              </div>
                            <p className="text-sm font-bold text-[var(--accent)] mt-1">{formatAmount(approval.amount)}</p>
                            <p className="text-xs text-[var(--ink-3)] mt-1 line-clamp-2">
                              {parsedApproval.description || 'No description'}
                            </p>
                            
                            {/* Date Information */}
                            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">Created:</span>
                                <span>{approval.created_at ? formatDateTime(approval.created_at).split(',')[0] : 'N/A'}</span>
                              </div>
                              {(approval.status === 'APPROVED' || approval.status === 'REJECTED') && getApprovalUpdatedAtValue(approval) && (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium">{approval.status === 'APPROVED' ? 'Approved:' : 'Rejected:'}</span>
                                    <span>{formatDateTime(String(getApprovalUpdatedAtValue(approval))).split(',')[0]}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium">by:</span>
                                    <span>{employeeNamesById[approval.approver_id] || getApproverName(approval.approver_id, approvers)}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-2 space-y-1 text-xs text-slate-600">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">Requester:</span>
                                <span>{getRequesterDisplayName(approval)}</span>
                              </div>
                            </div>
                            {canUsePendingApprovedTabs && approvalTab === 'approved' && isApprovedApprovalStatus(approval.status) && (
                              <div className="mt-2 space-y-1 text-xs text-slate-600">
                                <div className="flex items-center gap-1">
                                  <span className="font-medium">{approval.status === 'APPROVED' ? 'Approved at:' : 'Rejected at:'}</span>
                                  <span>{getApprovalUpdatedAtValue(approval) ? formatDateTime(String(getApprovalUpdatedAtValue(approval))) : 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="font-medium">Decision by:</span>
                                  <span>{employeeNamesById[approval.approver_id] || getApproverName(approval.approver_id, approvers)}</span>
                                </div>
                              </div>
                            )}
                          </button>

                          {isSelected && (
                            <div className="mt-4 border-t border-slate-200 pt-4">
                              <p className="text-xs text-slate-500">{parsedApproval.description || 'No description'}</p>
                              {renderLinkedTaskInfoBox(approval)}

                              {parsedApproval.attachments.length > 0 && (
                                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Attached Documents</p>
                                  <div className="mt-2 space-y-1.5">
                                    {parsedApproval.attachments.map((attachment, index) => (
                                      <a
                                        key={`${attachment.url}_${index}`}
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 hover:bg-slate-50"
                                      >
                                        <div className="min-w-0">
                                          <p className="text-xs font-semibold text-slate-700 truncate">{attachment.name}</p>
                                          <p className="text-[10px] text-slate-500">
                                            {attachment.contentType || 'File'}
                                            {attachment.size ? ` - ${formatAttachmentSize(attachment.size)}` : ''}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-1 text-slate-500 shrink-0">
                                          <Link2 className="w-3.5 h-3.5" />
                                          <Download className="w-3.5 h-3.5" />
                                        </div>
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {renderApprovalChat(approval)}

                              {canTakeActionOnApproval && (
                                <div className="mt-3 space-y-2">
                                  {/* Manager Escalation with Admin Selection */}
                                  {currentUser.role === 'manager' && !approval.isEscalated && (
                                    <div className="grid grid-cols-1 sm:grid-cols-[1.35fr_1fr] gap-2">
                                      <select
                                        value={selectedEscalationAdminByApproval[approval.id] || ''}
                                        onChange={(e) =>
                                          setSelectedEscalationAdminByApproval((prev) => ({
                                            ...prev,
                                            [approval.id]: e.target.value,
                                          }))
                                        }
                                        className="min-h-[44px] w-full px-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-base font-semibold hover:bg-slate-50 transition-all disabled:opacity-50"
                                      >
                                        <option value="">Select Super Admin...</option>
                                        {superAdmins.map(admin => (
                                          <option key={admin.id} value={admin.id}>
                                            {admin.name}
                                          </option>
                                        ))}
                                      </select>
                                      <LoadingButton
                                        type="button"
                                        onClick={() => void handleEscalateToAdmin(approval.id)}
                                        isLoading={isEscalating}
                                        loadingText="Escalating..."
                                        variant="success"
                                        disabled={updatingStatus || isProcessing || !(selectedEscalationAdminByApproval[approval.id] || '')}
                                        className="min-h-[44px] w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                      >
                                        <Send className="w-3 h-3 inline mr-1" />
                                        Confirm Escalation
                                      </LoadingButton>
                                    </div>
                                  )}
                                  
                                  {/* Admin Escalation Decision Buttons */}
                                  {isAdminApprover && approval.isEscalated && approval.adminEscalationStatus === 'PENDING' && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      <LoadingButton
                                        type="button"
                                        onClick={() => void handleAdminEscalationDecision('APPROVED')}
                                        isLoading={isAdminApproving}
                                        loadingText="Approving..."
                                        variant="success"
                                        disabled={updatingStatus || isProcessing}
                                        className="min-h-[44px] w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                      >
                                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                        Approve Escalation
                                      </LoadingButton>
                                      <LoadingButton
                                        type="button"
                                        onClick={() => void handleAdminEscalationDecision('REJECTED')}
                                        isLoading={isAdminRejecting}
                                        loadingText="Rejecting..."
                                        variant="danger"
                                        disabled={updatingStatus || isProcessing}
                                        className="min-h-[44px] w-full rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                      >
                                        <XCircle className="w-3 h-3 inline mr-1" />
                                        Reject Escalation
                                      </LoadingButton>
                                    </div>
                                  )}

                                  {/* Regular Approve/Reject for Admin after escalation approval */}
                                  {currentUser.role === 'manager' && approval.adminEscalationStatus === 'APPROVED' && (
                                    <LoadingButton
                                      type="button"
                                      onClick={() => void handleFinalApproval()}
                                      isLoading={isFinalApproving}
                                      loadingText="Approving..."
                                      variant="success"
                                      disabled={updatingStatus || isProcessing}
                                      className="min-h-[44px] w-full sm:w-auto px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                    >
                                      <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                      Approve
                                    </LoadingButton>
                                  )}

                                  {/* Regular Approve/Reject for non-escalated requests */}
	                                  {(!approval.isEscalated || canFinalizeAdminRejectedEscalation) && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      <LoadingButton
                                        type="button"
                                        onClick={() => void handleApprove(approval.id)}
                                        isLoading={isApproving}
                                        loadingText="Approving..."
                                        variant="success"
                                        disabled={updatingStatus || isProcessing}
                                        className="min-h-[44px] w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                      >
                                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                        Approve
                                      </LoadingButton>
                                      <LoadingButton
                                        type="button"
                                        onClick={() => void handleReject(approval.id)}
                                        isLoading={isRejecting}
                                        loadingText="Rejecting..."
                                        variant="danger"
                                        disabled={updatingStatus || isProcessing}
                                        className="min-h-[44px] w-full rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                      >
                                        <XCircle className="w-3 h-3 inline mr-1" />
                                        Reject
                                      </LoadingButton>
                                    </div>
                                  )}
                                </div>
                              )}

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            } else {
              // Original rendering for "Needs My Approval" view
              return filteredApprovals.map((approval) => {
                const isSelected = selectedApprovalId === approval.id;
	                const unreadCount = approvalUnreadCountById[approval.id] || 0;
		                const parsedApproval = extractApprovalAttachments(approval.description);
		                const directApprovalForCurrentUser = currentApproverIdSet.has(String(approval.approver_id || '').trim());
		                const escalatedApprovalForCurrentUser =
		                  isAdminApprover &&
		                  approval.isEscalated &&
		                  currentApproverIdSet.has(String(approval.escalated_to || '').trim());
		                const canFinalizeAdminRejectedEscalation =
		                  directApprovalForCurrentUser &&
		                  approval.adminEscalationStatus === 'REJECTED';
		                const canTakeActionOnApproval =
		                  isSelected &&
		                  !LOCKED_STATUSES.includes(approval.status) &&
		                  (
		                    directApprovalForCurrentUser ||
		                    (escalatedApprovalForCurrentUser && approval.adminEscalationStatus === 'PENDING') ||
		                    canFinalizeAdminRejectedEscalation
		                  );
                const isProcessing = processingApprovalId === approval.id;
                const isApproving = isProcessing && processingApprovalAction === 'approve';
                const isRejecting = isProcessing && processingApprovalAction === 'reject';
                const isEscalating = isProcessing && processingApprovalAction === 'escalate';
                const isAdminApproving = isProcessing && processingApprovalAction === 'admin_approve';
                const isAdminRejecting = isProcessing && processingApprovalAction === 'admin_reject';
                const isFinalApproving = isProcessing && processingApprovalAction === 'final_approve';
                const isReviewing = isProcessing && processingApprovalAction === 'review';

                return (
                  <div
                    key={approval.id}
                    className={`w-full rounded-2xl border p-4 transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)] ${
                      canUsePendingApprovedTabs && approvalTab === 'approved' && isApprovedApprovalStatus(approval.status)
                        ? 'border-emerald-200 bg-[var(--green-light)]/55 border-l-[3px] border-l-[var(--green)]'
                        : isSelected
                        ? 'border-amber-300 bg-[var(--orange-light)]/70 border-l-[3px] border-l-[var(--orange)]'
                        : 'border-[var(--border)] bg-white border-l-[3px] border-l-[var(--orange)]'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleApprovalSelection(approval.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-bold text-[var(--ink)] truncate">{approval.title || 'Untitled'}</p>
                        <div className="flex items-center gap-2">
                          {renderUnreadIndicator(unreadCount)}
                          <span className={`font-ui-mono text-[10px] font-medium uppercase px-2 py-1 rounded-full ${getStatusClasses(approval.status)}`}>
                            {approval.status}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-[var(--accent)] mt-1">{formatAmount(approval.amount)}</p>
                      <p className="text-xs text-[var(--ink-3)] mt-1 line-clamp-2">
                        {parsedApproval.description || 'No description'}
                      </p>
                      
                      {/* Date Information */}
                      <div className="mt-2 flex items-center justify-between text-xs text-[var(--ink-3)]">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Created:</span>
                          <span>{approval.created_at ? formatDateTime(approval.created_at).split(',')[0] : 'N/A'}</span>
                        </div>
                        {(approval.status === 'APPROVED' || approval.status === 'REJECTED') && getApprovalUpdatedAtValue(approval) && (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1">
                              <span className="font-medium">{approval.status === 'APPROVED' ? 'Approved:' : 'Rejected:'}</span>
                              <span>{formatDateTime(String(getApprovalUpdatedAtValue(approval))).split(',')[0]}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-medium">by:</span>
                              <span>{employeeNamesById[approval.approver_id] || getApproverName(approval.approver_id, approvers)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-slate-600">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Requester:</span>
                          <span>{getRequesterDisplayName(approval)}</span>
                        </div>
                      </div>
                      {canUsePendingApprovedTabs && approvalTab === 'approved' && isApprovedApprovalStatus(approval.status) && (
                        <div className="mt-2 space-y-1 text-xs text-slate-600">
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{approval.status === 'APPROVED' ? 'Approved at:' : 'Rejected at:'}</span>
                            <span>{getApprovalUpdatedAtValue(approval) ? formatDateTime(String(getApprovalUpdatedAtValue(approval))) : 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Decision by:</span>
                            <span>{employeeNamesById[approval.approver_id] || getApproverName(approval.approver_id, approvers)}</span>
                          </div>
                        </div>
                      )}
                    </button>

	                    {isSelected && (
	                      <div className="mt-4 border-t border-slate-200 pt-4">
	                        <p className="text-xs text-slate-500">{parsedApproval.description || 'No description'}</p>
                          {renderLinkedTaskInfoBox(approval)}

	                        {parsedApproval.attachments.length > 0 && (
                          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Attached Documents</p>
                            <div className="mt-2 space-y-1.5">
                              {parsedApproval.attachments.map((attachment, index) => (
                                <a
                                  key={`${attachment.url}_${index}`}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 hover:bg-slate-50"
                                >
                                  <div className="min-w-0">
                                    <p className="text-xs font-semibold text-slate-700 truncate">{attachment.name}</p>
                                    <p className="text-[10px] text-slate-500">
                                      {attachment.contentType || 'File'}
                                      {attachment.size ? ` - ${formatAttachmentSize(attachment.size)}` : ''}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 text-slate-500 shrink-0">
                                    <Link2 className="w-3.5 h-3.5" />
                                    <Download className="w-3.5 h-3.5" />
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {renderApprovalChat(approval)}

                        {/* Manager Escalation Banner */}
                        {approval.isEscalated && approval.adminEscalationStatus === 'PENDING' && currentUser.role === 'manager' && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs font-medium text-amber-800">
                              ⚠️ Escalated to Admin. Waiting for their review.
                            </p>
                          </div>
                        )}

                        {/* Admin Escalation Banner */}
                        {approval.isEscalated && isAdminApprover && (
                          <div className="mt-3 p-3 bg-[var(--accent-light)] border border-indigo-200 rounded-lg">
                            <p className="text-xs font-medium text-[var(--accent)]">
                              Escalated by {getApproverName(approval.approver_id, approvers)}
                            </p>
                          </div>
                        )}

                        {/* Admin Escalation Decision Banner */}
                        {approval.adminEscalationStatus === 'APPROVED' && currentUser.role === 'manager' && (
                          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <p className="text-xs font-medium text-emerald-800">
                              ✅ Admin Approved Escalation
                            </p>
                          </div>
                        )}

                        {canTakeActionOnApproval && (
                          <div className="mt-3 space-y-2">
                            {/* Manager Escalation with Admin Selection */}
                            {currentUser.role === 'manager' && !approval.isEscalated && (
                              <div className="grid grid-cols-1 sm:grid-cols-[1.35fr_1fr] gap-2">
                                <select
                                  value={selectedEscalationAdminByApproval[approval.id] || ''}
                                  onChange={(e) =>
                                    setSelectedEscalationAdminByApproval((prev) => ({
                                      ...prev,
                                      [approval.id]: e.target.value,
                                    }))
                                  }
                                  className="min-h-[44px] w-full px-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-base font-semibold hover:bg-slate-50 transition-all disabled:opacity-50"
                                >
                                  <option value="">Select Super Admin...</option>
                                  {superAdmins.map((admin) => (
                                    <option key={admin.id} value={admin.id}>
                                      {admin.name}
                                    </option>
                                  ))}
                                </select>
                                <LoadingButton
                                  type="button"
                                  onClick={() => void handleEscalateToAdmin(approval.id)}
                                  isLoading={isEscalating}
                                  loadingText="Escalating..."
                                  variant="success"
                                  disabled={updatingStatus || isProcessing || !(selectedEscalationAdminByApproval[approval.id] || '')}
                                  className="min-h-[44px] w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                >
                                  <Send className="w-3 h-3 inline mr-1" />
                                  Confirm Escalation
                                </LoadingButton>
                              </div>
                            )}
                            
                            {/* Admin Escalation Decision Buttons */}
                            {isAdminApprover && approval.isEscalated && approval.adminEscalationStatus === 'PENDING' && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <LoadingButton
                                  type="button"
                                  onClick={() => void handleAdminEscalationDecision('APPROVED')}
                                  isLoading={isAdminApproving}
                                  loadingText="Approving..."
                                  variant="success"
                                  disabled={updatingStatus || isProcessing}
                                  className="min-h-[44px] w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                >
                                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                  Approve Escalation
                                </LoadingButton>
                                <LoadingButton
                                  type="button"
                                  onClick={() => void handleAdminEscalationDecision('REJECTED')}
                                  isLoading={isAdminRejecting}
                                  loadingText="Rejecting..."
                                  variant="danger"
                                  disabled={updatingStatus || isProcessing}
                                  className="min-h-[44px] w-full rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                >
                                  <XCircle className="w-3 h-3 inline mr-1" />
                                  Reject Escalation
                                </LoadingButton>
                              </div>
                            )}

                            {/* Regular Approve/Reject for Admin after escalation approval */}
                            {currentUser.role === 'manager' && approval.adminEscalationStatus === 'APPROVED' && (
                              <LoadingButton
                                type="button"
                                onClick={() => void handleFinalApproval()}
                                isLoading={isFinalApproving}
                                loadingText="Approving..."
                                variant="success"
                                disabled={updatingStatus || isProcessing}
                                className="min-h-[44px] w-full sm:w-auto px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                              >
                                <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                Approve
                              </LoadingButton>
                            )}

                            {/* Regular Approve/Reject for non-escalated requests */}
	                            {(!approval.isEscalated || canFinalizeAdminRejectedEscalation) && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <LoadingButton
                                  type="button"
                                  onClick={() => void handleApprove(approval.id)}
                                  isLoading={isApproving}
                                  loadingText="Approving..."
                                  variant="success"
                                  disabled={updatingStatus || isProcessing}
                                  className="min-h-[44px] w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                >
                                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                  Approve
                                </LoadingButton>
                                <LoadingButton
                                  type="button"
                                  onClick={() => void handleReject(approval.id)}
                                  isLoading={isRejecting}
                                  loadingText="Rejecting..."
                                  variant="danger"
                                  disabled={updatingStatus || isProcessing}
                                  className="min-h-[44px] w-full rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-all disabled:opacity-50"
                                >
                                  <XCircle className="w-3 h-3 inline mr-1" />
                                  Reject
                                </LoadingButton>
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                );
              });
            }
          })()
        )}
      </div>

      {selectedApprovalTask && (
        <TaskDetailsScreen
          task={selectedApprovalTask}
          subTasks={selectedApprovalSubTasks}
          parentTask={selectedApprovalParentTask}
          employees={companyEmployees}
          currentUser={currentUser}
          readOnly
          onBack={() => setViewingTaskApprovalId(null)}
          onStartTask={() => {}}
          onReopenTask={() => {}}
          onCompleteTask={async () => {}}
          onCompleteTaskWithoutPhoto={async () => {}}
          onReassign={() => {}}
          onDelegate={() => {}}
          onDelete={async () => {}}
        />
      )}

    </section>
  );
};

export default ApprovalsPanel;



